/**
 * Product Page JS — Handles gallery, swatches, size, quantity, cart, wishlist
 */
(function () {
  'use strict';

  /* ── HELPERS ── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }
  function formatMoney(cents) {
    return window.AykahFormatMoney ? window.AykahFormatMoney(cents) : '$' + (cents / 100).toFixed(2);
  }

  /* ── STATE ── */
  var state = {
    selectedImage: 0,
    zoomLevel: 1,
    zoomPanX: 0.5,
    zoomPanY: 0.5,
    quantity: 1,
    variantId: null,
    productData: null,
    selectedOptions: {}
  };

  /* Variant microinteraction state. A monotonically increasing token ensures
     rapid colour clicks can never finish an older gallery transition after a
     newer selection has already been made. */
  var galleryVariantTransitionId = 0;
  var galleryVariantAnimation = null;

  /* ── INIT ── */
  function init() {
    var jsonEl = qs('[data-product-json]');
    if (!jsonEl) return;

    try {
      state.productData = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    var atcBtn = qs('[data-add-to-cart]');
    if (atcBtn) {
      state.variantId = parseInt(atcBtn.dataset.variantId, 10);
    }

    // Determine initial selected options from current variant
    if (state.productData.variants.length) {
      var currentVariant = state.productData.variants.find(function (v) {
        return v.id === state.variantId;
      }) || state.productData.variants[0];

      if (currentVariant) {
        if (currentVariant.option1) state.selectedOptions['option1'] = currentVariant.option1;
        if (currentVariant.option2) state.selectedOptions['option2'] = currentVariant.option2;
        if (currentVariant.option3) state.selectedOptions['option3'] = currentVariant.option3;
      }
    }

    /* Build the first gallery from colour ownership before any lazy preload.
       This prevents another colour's images flashing into the active sequence. */
    syncGalleryForSelectedColor();

    bindThumbnails();
    /* The colour the page opened on decides which sizes are real, so this runs
       before anything is bound rather than waiting for the first swatch click. */
    applySizeAvailability(state.selectedOptions[getColorPosition()]);
    bindImageZoom();

    /* What the photo viewer reads from the gallery. It asks rather than
       re-deriving, so it always shows exactly the photos, in exactly the
       order, that the colour and orientation filters left visible. */
    window.AykahGallery = {
      visibleImages: function () { return getVisibleMainImages(); },
      currentIndex: function () { return state.selectedImage; },
      select: function (idx, opts) {
        if (!(opts && opts.instant)) { setActiveImage(idx); return; }
        // The viewer is flying its photo back into this spot: land there at
        // once, with no crossfade or rail glide showing through the flight.
        var slides = qsa('[data-main-image]');
        slides.forEach(function (im) { im.style.transition = 'none'; });
        railSyncing = true;
        setActiveImage(idx);
        railSyncing = false;
        if (isMobileGallery()) railScrollToIndex(idx, false);
        window.setTimeout(function () {
          slides.forEach(function (im) { im.style.transition = ''; });
        }, 60);
      },
      colourName: function () {
        return getColorOption() ? (state.selectedOptions[getColorPosition()] || '') : '';
      }
    };
    bindColorSwatches();
    bindSizeSelector();
    bindOption3Selector();
    bindOrientationSelector();
    bindGenericOptionSelectors();
    initLoyalLine();
    bindQuantity();
    bindAddToCart();
    bindWishlist();
    trackRecentlyViewed();

    /* Publish the variant the page opened on, so sections below the hero start
       in step instead of waiting for the shopper's first click. */
    if (state.productData.variants.length) {
      var openingVariant = state.productData.variants.find(function (v) {
        return v.id === state.variantId;
      }) || state.productData.variants[0];
      publishVariantChange(openingVariant);
    }
  }

  /* ── RECENTLY VIEWED TRACKING ── */
  function trackRecentlyViewed() {
    var handle = null;
    var btns = qsa('[data-wishlist-toggle]');
    if (btns.length) handle = btns[0].dataset.productHandle;
    if (!handle) {
      /* Fallback: extract from canonical URL */
      var canonical = qs('link[rel="canonical"]');
      if (canonical) {
        var parts = canonical.href.split('/products/');
        if (parts[1]) handle = parts[1].split('?')[0].split('#')[0];
      }
    }
    if (!handle) return;

    try {
      var viewed = JSON.parse(localStorage.getItem('aykah_recently_viewed') || '[]');
      /* Remove if already exists (move to front) */
      viewed = viewed.filter(function (h) { return h !== handle; });
      viewed.unshift(handle);
      /* Keep max 12 items */
      if (viewed.length > 12) viewed = viewed.slice(0, 12);
      localStorage.setItem('aykah_recently_viewed', JSON.stringify(viewed));
    } catch (e) {
      /* localStorage quota exceeded or unavailable */
    }

    /* The browsing PROFILE is a second, separate key on purpose.
       aykah_recently_viewed stays a plain array of handles because the 404
       page reads it that way (error-404.js), and changing its shape would
       blank that strip for anyone holding the old value. */
    try {
      var blob = qs('[data-product-json]');
      if (!blob) return;
      var meta = JSON.parse(blob.textContent);
      var profile = JSON.parse(localStorage.getItem('aykah_browse_profile') || '[]');
      profile = profile.filter(function (p) { return p && p.h !== handle; });
      profile.unshift({
        h: handle,
        t: meta.type || '',
        g: (meta.tags || '').split('|').filter(Boolean).slice(0, 8),
        p: meta.price || 0
      });
      if (profile.length > 12) profile = profile.slice(0, 12);
      localStorage.setItem('aykah_browse_profile', JSON.stringify(profile));
    } catch (e) {
      /* A missing or malformed blob must never break the page. The related
         row falls back to its server-rendered order when the profile is
         absent, so there is nothing to recover here. */
    }
  }

  /* ── GALLERY / THUMBNAILS ── */
  function bindThumbnails() {
    var btns = qsa('[data-thumbnail-btn]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.dataset.index, 10);
        setActiveImage(idx);

        // On desktop, selecting a thumbnail also activates the gallery so the
        // physical arrow keys can continue from that image immediately.
        if (!isMobileGallery()) {
          var wrap = getZoomWrap();
          if (wrap && document.activeElement !== wrap) {
            try {
              wrap.focus({ preventScroll: true });
            } catch (e) {
              wrap.focus();
            }
          }
        }
      });
      // Preload a slide's main image on hover/focus intent, before the click.
      var preloadOnIntent = function () {
        var i = parseInt(btn.dataset.index, 10);
        var m = document.querySelector('[data-main-image][data-index="' + i + '"]');
        if (m) hydrateMain(m);
      };
      btn.addEventListener('mouseenter', preloadOnIntent);
      btn.addEventListener('focus', preloadOnIntent);
    });

    // Preload the 2nd main image during idle time so the first swipe is instant,
    // without competing with the LCP (first) image at page load.
    var preloadSecond = function () {
      var visibleImages = getVisibleMainImages();
      var second = visibleImages[1];
      if (second) hydrateMain(second);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadSecond, { timeout: 3000 });
    } else {
      window.addEventListener('load', function () { setTimeout(preloadSecond, 1000); });
    }
  }

  // Hydrate a main gallery image on demand. Non-active slides ship with only
  // data-src/data-srcset (no real src) so they don't download at page load;
  // this swaps the real URL in the first time a slide is opened or preloaded.
  function hydrateMain(img) {
    if (!img || img.getAttribute('data-hydrated') === '1') return;
    // First slide already carries a real src (the LCP image) — nothing to do.
    if (img.getAttribute('src')) { img.setAttribute('data-hydrated', '1'); return; }
    var ds = img.getAttribute('data-src');
    if (!ds) return;
    var dss = img.getAttribute('data-srcset');
    if (dss) img.setAttribute('srcset', dss);
    img.setAttribute('src', ds);
    img.setAttribute('data-hydrated', '1');
  }

  function isMobileGallery() {
    return window.matchMedia('(max-width: 1023px)').matches;
  }

  /* ── The gallery rail ──────────────────────────────────────────────────
     Under 1024px the stylesheet turns the viewport into a scroll-snap rail,
     so the browser owns the motion and this code follows it rather than
     driving it. Everything below is inert on desktop.

     Zoomed in, the rail is locked by CSS and the pan gesture takes over, so
     the rail is not "on" simply because the screen is small. */
  function railActive() {
    var wrap = getZoomWrap();
    return isMobileGallery() && !!wrap && !wrap.classList.contains('is-zoomed');
  }

  /* Set while the rail is being moved by script rather than by a finger, so
     the scroll it causes is not read back as the shopper choosing an image. */
  var railSyncing = false;

  function railScrollToIndex(idx, smooth) {
    var wrap = getZoomWrap();
    var img = qs('[data-main-image][data-index="' + idx + '"]');
    if (!wrap || !img || !isMobileGallery()) return;
    var left = img.offsetLeft - (wrap.clientWidth - img.offsetWidth) / 2;
    if (Math.abs(wrap.scrollLeft - left) < 2) return;

    railSyncing = true;
    wrap.classList.add('is-syncing');
    wrap.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
    /* Released once the scroll it started has stopped arriving. */
    clearTimeout(railSyncTimer);
    railSyncTimer = setTimeout(function () {
      railSyncing = false;
      wrap.classList.remove('is-syncing');
    }, smooth ? 420 : 80);
  }
  var railSyncTimer = null;

  /* Which slide is under the middle of the rail right now. */
  function railIndexFromScroll() {
    var wrap = getZoomWrap();
    var visible = getVisibleMainImages();
    if (!wrap || !visible.length) return null;
    var mid = wrap.scrollLeft + wrap.clientWidth / 2;
    var best = visible[0], bestDist = Infinity;
    visible.forEach(function (img) {
      var c = img.offsetLeft + img.offsetWidth / 2;
      var d = Math.abs(c - mid);
      if (d < bestDist) { bestDist = d; best = img; }
    });
    return parseInt(best.dataset.index, 10);
  }

  /* Only the first image ships with a real src, so that the PDP downloads one
     photo at load and the LCP stays where it is. On a rail the shopper can
     reach the third slide in a second, so hydration follows the scroll: the
     slide being looked at, and the one on either side of it, which is enough
     lead time at any speed a thumb can manage. */
  function hydrateAround(idx) {
    var visible = getVisibleMainImages();
    var at = visible.findIndex(function (img) {
      return parseInt(img.dataset.index, 10) === idx;
    });
    if (at < 0) return;
    [at - 1, at, at + 1].forEach(function (i) {
      if (visible[i]) hydrateMain(visible[i]);
    });
  }

  function getZoomWrap() {
    return qs('[data-image-zoom]');
  }

  /* ── Photo viewer hand-off ─────────────────────────────────────────────
     A click, a single tap or Enter on the gallery opens the full-screen
     viewer (assets/pdp-photo-viewer.js), where the 2x zoom now lives. When
     the viewer is switched off in the theme editor, or its script failed to
     load, this returns false and the old in-place zoom answers instead. */
  function openPhotoViewer() {
    var viewer = window.AykahPhotoViewer;
    if (!viewer || typeof viewer.open !== 'function') return false;
    // The viewer declines (false) when it cannot open cleanly, e.g. while the
    // page is pinch-zoomed; the in-place zoom then answers as before.
    return viewer.open({ index: state.selectedImage }) !== false;
  }

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animateVariantControl(control) {
    if (!control || prefersReducedMotion() || typeof control.animate !== 'function') return;

    control.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(0.965)' },
        { transform: 'scale(1)' }
      ],
      {
        duration: 220,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    );
  }

  function transitionGalleryForVariantChange(applyChange) {
    var wrap = getZoomWrap();
    if (!wrap || prefersReducedMotion() || typeof wrap.animate !== 'function') {
      applyChange();
      return;
    }

    var transitionId = ++galleryVariantTransitionId;

    if (galleryVariantAnimation) {
      galleryVariantAnimation.cancel();
      galleryVariantAnimation = null;
    }

    galleryVariantAnimation = wrap.animate(
      [
        { opacity: 1, transform: 'translateY(0) scale(1)' },
        { opacity: 0.74, transform: 'translateY(2px) scale(0.997)' }
      ],
      {
        duration: 130,
        easing: 'cubic-bezier(0.4, 0, 1, 1)',
        fill: 'forwards'
      }
    );

    galleryVariantAnimation.onfinish = function () {
      if (transitionId !== galleryVariantTransitionId) return;

      applyChange();

      var activeImage = getActiveMainImage();
      if (activeImage) hydrateMain(activeImage);

      galleryVariantAnimation = wrap.animate(
        [
          { opacity: 0.74, transform: 'translateY(2px) scale(0.997)' },
          { opacity: 1, transform: 'translateY(0) scale(1)' }
        ],
        {
          duration: 230,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'both'
        }
      );

      galleryVariantAnimation.onfinish = function () {
        if (transitionId !== galleryVariantTransitionId) return;
        galleryVariantAnimation.cancel();
        galleryVariantAnimation = null;
      };
    };
  }

  function getActiveMainImage() {
    return qs('[data-main-image][data-index="' + state.selectedImage + '"]');
  }

  function getVisibleMainImages() {
    return Array.prototype.filter.call(qsa('[data-main-image]'), function (img) {
      return !img.hidden;
    }).sort(function (a, b) {
      var orderDifference = galleryMediaOrder(a) - galleryMediaOrder(b);
      if (orderDifference) return orderDifference;

      // Keep Shopify Admin order when two images belong to the same group.
      return parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10);
    });
  }

  function normalizeGalleryText(value) {
    var text = String(value || '').toLowerCase();
    if (text.normalize) {
      text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return text
      .replace(/&amp;/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function galleryTextHas(signature, term) {
    signature = normalizeGalleryText(signature);
    term = normalizeGalleryText(term);
    return !!term && (' ' + signature + ' ').indexOf(' ' + term + ' ') !== -1;
  }

  function galleryImageKey(value) {
    if (!value) return '';
    try {
      return decodeURIComponent(new URL(value, window.location.origin).pathname)
        .toLowerCase();
    } catch (e) {
      return String(value).split('?')[0].toLowerCase();
    }
  }

  function getColorOption() {
    var found = null;
    if (!state.productData || !state.productData.options_with_values) return found;
    state.productData.options_with_values.forEach(function (opt) {
      var name = normalizeGalleryText(opt.name);
      if (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1) {
        found = opt;
      }
    });
    return found;
  }

  /* ── ORIENTATION AS A GALLERY FILTER ──
     A sectional's Left Facing / Right Facing option partitions its photographs
     exactly the way colour does: a filename or alt naming one side never
     belongs to the other. So the orientation option is handed to the existing
     colour matcher rather than given a second matcher of its own. Colour still
     wins when a product has both, which keeps every colour product unchanged.

     The name test mirrors sections/product-hero.liquid, which already draws the
     picker for these products. The left/right value test covers sectionals whose
     option carries some other name entirely. */
  function galleryOptionIsOrientation(option) {
    var name = normalizeGalleryText(option && option.name);
    if (
      name.indexOf('orientation') !== -1 ||
      name.indexOf('configuration') !== -1 ||
      name.indexOf('direction') !== -1 ||
      name.indexOf('chaise') !== -1 ||
      name.indexOf('facing') !== -1
    ) {
      return true;
    }

    var hasLeft = false;
    var hasRight = false;
    ((option && option.values) || []).forEach(function (value) {
      var v = normalizeGalleryText(value);
      if (galleryTextHas(v, 'left') || v === 'laf' || v === 'lhf' || v === 'lhs') hasLeft = true;
      if (galleryTextHas(v, 'right') || v === 'raf' || v === 'rhf' || v === 'rhs') hasRight = true;
    });
    return hasLeft && hasRight;
  }

  /* Orientation filtering only switches on when every value is nameable from at
     least one image. Juliette's photographs carry no side in their names and
     Kova has no left-facing photograph at all, so filtering those would drop the
     gallery onto its single-image fallback — worse than the unfiltered gallery
     they show today. Those products keep their current behaviour untouched. */
  function getOrientationOption() {
    if (!state.productData || !state.productData.options_with_values) return null;

    var found = null;
    state.productData.options_with_values.forEach(function (option) {
      if (!found && galleryOptionIsOrientation(option)) found = option;
    });
    if (!found || !found.values || found.values.length < 2) return null;

    var images = Array.prototype.slice.call(qsa('[data-main-image]'));
    if (!images.length) return null;

    var everyValueIsNamed = found.values.every(function (value) {
      return images.some(function (img) {
        return galleryTextHas(galleryMediaSignature(img), value);
      });
    });

    return everyValueIsNamed ? found : null;
  }

  function getGalleryFilterOption() {
    var color = getColorOption();
    if (color) return { option: color, mode: 'color' };

    var orientation = getOrientationOption();
    if (orientation) return { option: orientation, mode: 'orientation' };

    return { option: null, mode: null };
  }

  /* ── ORIENTATION AS A SECOND AXIS ──
     getGalleryFilterOption() names exactly one owner and colour wins whenever a
     product has both, so on the three products carrying a colour AND a
     Left/Right option (Araam, Liva, Mellow) the side buttons moved the price,
     the URL and the availability while the gallery sat still. Araam's Left
     Facing / Natural Dune showed the right-facing front photograph.

     Colour still owns the filter. Orientation runs afterwards and only ever
     NARROWS what colour already allowed, so no colour product changes.

     Kept deliberately generous: an image that names no side, or that is
     attached to variants on both sides, stays visible on both. That is the same
     rule orientation-only products have always followed, and it is what stops a
     room scene or a fabric close-up from disappearing when a side is picked. */
  function getSecondaryOrientationOption() {
    /* Null when orientation is already the primary filter, so single-axis
       products keep running down exactly the path they ran down before. */
    if (!getColorOption()) return null;
    return getOrientationOption();
  }

  function galleryOrientationValues() {
    var option = getSecondaryOrientationOption();
    return option && option.values ? option.values.slice() : [];
  }

  function galleryColorValues() {
    var option = getGalleryFilterOption().option;
    return option && option.values ? option.values.slice() : [];
  }

  function getSizeOption() {
    var found = null;
    if (!state.productData || !state.productData.options_with_values) return found;
    state.productData.options_with_values.forEach(function (opt) {
      var name = normalizeGalleryText(opt.name);
      if (name.indexOf('size') !== -1 || name.indexOf('seater') !== -1) {
        found = opt;
      }
    });
    return found;
  }

  function gallerySizeValues() {
    var option = getSizeOption();
    return option && option.values ? option.values.slice() : [];
  }

  function galleryMediaSignature(element) {
    return normalizeGalleryText(
      (element.dataset.galleryAlt || '') + ' ' +
      (element.dataset.galleryFilename || '')
    );
  }

  function galleryVariantSizes(element) {
    var sizes = [];
    String(element.dataset.galleryVariantSizes || '')
      .split('||')
      .map(normalizeGalleryText)
      .filter(Boolean)
      .forEach(function (size) {
        if (sizes.indexOf(size) === -1) sizes.push(size);
      });
    return sizes;
  }

  function galleryMatchedSize(element, sizes) {
    var normalizedSizes = sizes.map(normalizeGalleryText);
    var attached = galleryVariantSizes(element);
    var signature = galleryMediaSignature(element);
    var signatureMatches = normalizedSizes.filter(function (size) {
      return galleryTextHas(signature, size);
    });

    /* A direct image-to-variant association is the strongest signal when that
       image belongs to one size only. If Shopify associates it with several
       sizes, let the filename or alt text disambiguate instead. */
    var attachedMatches = normalizedSizes.filter(function (size) {
      return attached.indexOf(size) !== -1;
    });
    if (attachedMatches.length === 1) return attachedMatches[0];

    if (attachedMatches.length > 1) {
      var attachedSignatureMatches = signatureMatches.filter(function (size) {
        return attachedMatches.indexOf(size) !== -1;
      });
      if (attachedSignatureMatches.length === 1) {
        return attachedSignatureMatches[0];
      }
    }

    if (!signatureMatches.length) return '';

    /* Prefer the most specific label when option values overlap, for example
       King and California King. */
    signatureMatches.sort(function (a, b) {
      var wordDifference = b.split(' ').length - a.split(' ').length;
      return wordDifference || b.length - a.length;
    });
    return signatureMatches[0];
  }

  function galleryViewOrder(element) {
    var signature = galleryMediaSignature(element);
    if (
      galleryTextHas(signature, 'front') ||
      galleryTextHas(signature, 'hero')
    ) return 10;
    if (
      galleryTextHas(signature, 'side') ||
      galleryTextHas(signature, 'profile')
    ) return 20;
    if (
      galleryTextHas(signature, 'back') ||
      galleryTextHas(signature, 'rear')
    ) return 30;
    return 40;
  }

  /*
   * PDP sequence after colour filtering:
   * lifestyle/shared first, then a complete e-commerce set for each size.
   * Within a size the order is front, side, back, then other angles/details.
   * Unknown-size e-commerce media retains Shopify Admin order so two sizes are
   * never mixed based on a guess.
   */
  /* The rank Liquid rendered with, so the client sorts the way the server
     already did. Without it these two disagreed and the painted order won.

     galleryMediaOrder used to return 0 for anything whose kind was not 'ecom',
     and _kind falls back to 'shared' for any filename pdp-gallery-item's
     heuristics do not recognise. '-closeup' and '-top' are not in that list, so
     on Blanco Black every closeup scored 0 and was painted above the front
     view, which scored 100000. Liquid had the order right; flex `order`
     overruled it. */
  function galleryRank(element) {
    var raw = element.dataset.galleryRank;
    if (raw === undefined || raw === '') return 1;
    var n = parseInt(raw, 10);
    return isNaN(n) ? 1 : n;
  }

  function galleryMediaOrder(element) {
    /* Rank dominates. The size/view sequencing below only ever reorders images
       WITHIN a rank, which is what it was always meant to do. */
    var base = galleryRank(element) * 1000000;

    if (element.dataset.galleryKind !== 'ecom') return base;

    var sizes = gallerySizeValues();
    var viewOrder = galleryViewOrder(element);
    if (sizes.length < 2) return base + viewOrder;

    /* 100000 is the "size not named in the filename" bucket. It stays below the
       1000000 rank step on purpose, so an unmatched image drifts to the end of
       its OWN rank rather than escaping into the next one. */
    var matchedSize = galleryMatchedSize(element, sizes);
    if (!matchedSize) return base + 100000;

    var normalizedSizes = sizes.map(normalizeGalleryText);
    var sizeIndex = normalizedSizes.indexOf(matchedSize);
    if (sizeIndex < 0) return base + 100000;

    return base + ((sizeIndex + 1) * 100) + viewOrder;
  }

  function galleryVariantColors(element) {
    return String(element.dataset.galleryVariantColors || '')
      .split('||')
      .map(normalizeGalleryText)
      .filter(Boolean);
  }

  function galleryVariantOrientations(element) {
    return String(element.dataset.galleryVariantOrientations || '')
      .split('||')
      .map(normalizeGalleryText)
      .filter(Boolean);
  }

  /* Which side, if any, this image belongs to. An empty result means "both",
     never "neither", which is why the caller treats it as visible. */
  function galleryMatchedOrientations(element, orientations) {
    var normalized = orientations.map(normalizeGalleryText);
    var attached = [];

    galleryVariantOrientations(element).forEach(function (value) {
      if (normalized.indexOf(value) === -1) return;
      if (attached.indexOf(value) === -1) attached.push(value);
    });

    /* A Shopify variant association is a merchandising statement and outranks
       the filename, so attaching a photo to both sides publishes it on both
       even when its name still carries the side it was shot from. That is how
       a shared close-up escapes a '-right_Facing' filename without a re-upload,
       which matters because Shopify will not overwrite a filename. */
    if (attached.length === 1) return attached;
    if (attached.length > 1) return [];

    var signature = galleryMediaSignature(element);
    return normalized.filter(function (value) {
      return galleryTextHas(signature, value);
    });
  }

  function galleryOrientationIsVisible(element, selectedOrientation, orientations) {
    if (!selectedOrientation || orientations.length < 2) return true;
    var matches = galleryMatchedOrientations(element, orientations);
    /* Names no side, or names both: the same sofa either way, so keep it. */
    if (matches.length !== 1) return true;
    return matches[0] === selectedOrientation;
  }

  /* ── SIZE AS A THIRD AXIS ──
     Ruthik: "i am seeing 92 in images for 71 selected". Size already ordered the
     gallery but never filtered it, so a 71 inch shopper was shown the 94 inch
     table's photographs too. Ordering them later was not enough.

     Same shape as orientation, and for the same reasons. It only ever NARROWS
     what colour already allowed, and an image that names no size stays visible,
     so lifestyle shots and shared detail crops never disappear. */
  function getFilterableSizeOption() {
    var option = getSizeOption();
    if (!option || !option.values || option.values.length < 2) return null;

    /* Only filter when EVERY size is nameable from at least one image. A product
       photographed in one size only would otherwise empty the gallery for the
       other, which is worse than the mixed gallery it shows today. This is the
       same guard getOrientationOption() uses, and for the same reason. */
    var images = Array.prototype.slice.call(qsa('[data-main-image]'));
    if (!images.length) return null;

    var normalized = option.values.map(normalizeGalleryText);
    var everyValueIsNamed = normalized.every(function (value) {
      return images.some(function (img) {
        return galleryMatchedSize(img, option.values) === value;
      });
    });

    return everyValueIsNamed ? option : null;
  }

  function gallerySizeIsVisible(element, selectedSize, sizes) {
    if (!selectedSize || sizes.length < 2) return true;
    var matched = galleryMatchedSize(element, sizes);
    /* Names no size: shared between them, so keep it. */
    if (!matched) return true;
    return matched === selectedSize;
  }

  function galleryAliasStopWords() {
    var stop = {};
    var generic = [
      'angle', 'angled', 'back', 'close', 'closed', 'closeup', 'cutout',
      'detail', 'ecom', 'front', 'hero', 'image', 'img', 'lifestyle',
      'open', 'opened', 'perspective', 'rear', 'render', 'room', 'scene',
      'shot', 'side', 'styled', 'view', 'with', 'without',
      'jpg', 'jpeg', 'png', 'webp', 'avif', 'gif',
      /* Sentence filler. Alt text is written for a screen reader, not for this
         matcher, so a phrase such as "Close-up detail of the Mellow Sofa"
         reduces to the single word "the" once the real words are removed. A
         colour is never called "the". */
      'the', 'and', 'for', 'from', 'this', 'that', 'into', 'onto'
    ];

    generic.forEach(function (word) { stop[word] = true; });

    normalizeGalleryText(state.productData && state.productData.title)
      .split(' ')
      .forEach(function (word) { if (word) stop[word] = true; });

    var filterOption = getGalleryFilterOption().option;
    if (state.productData && state.productData.options_with_values) {
      state.productData.options_with_values.forEach(function (option) {
        if (filterOption && option.position === filterOption.position) return;
        (option.values || []).forEach(function (value) {
          normalizeGalleryText(value).split(' ').forEach(function (word) {
            if (word) stop[word] = true;
          });
        });
      });
    }

    return stop;
  }

  function galleryAliasCandidate(value, stopWords) {
    var words = normalizeGalleryText(value).split(' ').filter(function (word) {
      if (!word || word.length < 3 || stopWords[word]) return false;
      if (/\d/.test(word) || /^[a-f]{6,}$/.test(word)) return false;
      return true;
    });
    return words.join(' ');
  }

  /* Some older products kept a previous colour name in image metadata after the
     variant label changed, for example Silver Mist images now sold as Grey.
     Learn that alias from each colour's own featured image instead of hardcoding
     product-specific exceptions. */
  function buildGalleryColorAliases(colors, colorPosition) {
    var aliases = {};
    var stopWords = galleryAliasStopWords();
    var mainImages = Array.prototype.slice.call(qsa('[data-main-image]'));

    colors.forEach(function (color) {
      aliases[normalizeGalleryText(color)] = [normalizeGalleryText(color)];
    });

    (state.productData.variants || []).forEach(function (variant) {
      var rawColor = variant[colorPosition];
      var color = normalizeGalleryText(rawColor);
      if (!color || !aliases[color] || !variant.featured_image) return;

      var key = galleryImageKey(variant.featured_image);
      var anchor = mainImages.find(function (img) {
        return galleryImageKey(img.getAttribute('data-src')) === key;
      });
      if (!anchor) return;

      [
        anchor.dataset.galleryAlt || '',
        anchor.dataset.galleryFilename || ''
      ].forEach(function (source) {
        var alias = galleryAliasCandidate(source, stopWords);
        if (!alias || aliases[color].indexOf(alias) !== -1) return;

        /* A variant pinned to the wrong photo would otherwise teach this colour
           another colour's name. Daisy Armchair had Coconut Bouclé pinned to
           daisy-armchair-meringue-boucle-front.jpg, so Coconut learned the alias
           "daisy armchair meringue boucle"; every Meringue photo then matched two
           colours at once and galleryMatchedColors() dropped it as ambiguous,
           hiding 5 of 11 images on EVERY colour. A pin is a merchandising choice
           made in Admin, so it can be wrong; a colour's own name never is. Refuse
           to learn an alias that carries a different colour's name. */
        var borrowed = colors.some(function (other) {
          var normalizedOther = normalizeGalleryText(other);
          return normalizedOther !== color && galleryTextHas(alias, normalizedOther);
        });
        if (borrowed) return;

        /* An alias is only worth learning if it points at ONE colour. Mellow's
           Beige variant is pinned to a photo whose alt text is "Boucle Mellow
           Sofa, front view": strip the title and the view words and the alias
           becomes "boucle", the material every Mellow shares. It sat on 24 of
           the 25 gallery images, so Beige silently claimed the whole gallery.
           Reject a candidate that also appears on a photo naming another colour
           outright, which is exactly what a material or a finish word does. */
        var notDistinctive = mainImages.some(function (image) {
          var otherSignature = galleryMediaSignature(image);
          if (!galleryTextHas(otherSignature, alias)) return false;
          return colors.some(function (other) {
            var normalizedOther = normalizeGalleryText(other);
            return (
              normalizedOther !== color &&
              galleryTextHas(otherSignature, normalizedOther)
            );
          });
        });
        if (notDistinctive) return;

        aliases[color].push(alias);
      });
    });

    return aliases;
  }

  function galleryMatchedColors(element, colors, aliases) {
    var attached = galleryVariantColors(element);
    if (attached.length) {
      return colors.map(normalizeGalleryText).filter(function (color) {
        return attached.indexOf(color) !== -1;
      });
    }

    var signature = galleryMediaSignature(element);
    var matches = [];

    colors.forEach(function (color) {
      var normalizedColor = normalizeGalleryText(color);
      (aliases[normalizedColor] || [normalizedColor]).forEach(function (term) {
        if (galleryTextHas(signature, term)) {
          matches.push({
            color: normalizedColor,
            term: term,
            /* A photo that states a real colour name is answering the question
               directly. A learned alias is only ever an inference drawn from one
               pinned photo, so it must never outrank the name itself. */
            exact: term === normalizedColor
          });
        }
      });
    });

    if (!matches.length) return [];

    matches.sort(function (a, b) {
      /* Rank a stated colour above any learned alias before considering length.
         Mellow taught Beige the alias "boucle", and the old length-only sort let
         "boucle" (6 characters) beat "grey" (4), so correctly named grey photos
         were handed to Beige. Length only decides between terms of equal
         standing, where a longer label really is the more specific one, as with
         "cashew cream" over "cream". */
      if (a.exact !== b.exact) return a.exact ? -1 : 1;
      var wordDifference = b.term.split(' ').length - a.term.split(' ').length;
      return wordDifference || b.term.length - a.term.length;
    });

    var bestTerm = matches[0].term;
    var bestColors = [];
    matches.forEach(function (match) {
      if (match.term !== bestTerm) return;
      if (bestColors.indexOf(match.color) === -1) bestColors.push(match.color);
    });

    /* An ambiguous legacy label is not safe colour ownership. Treat it as
       unclassified so an e-commerce shot is hidden instead of mixed. */
    return bestColors.length === 1 ? bestColors : [];
  }

  function galleryMediaIsVisible(element, selectedColor, colors, aliases, mode) {
    /* Explicitly shared media stays visible for every colour. Lifestyle and
       e-commerce media both use the same ownership signals: Shopify variant
       associations first, then colour names and learned aliases in metadata. */
    if (element.dataset.galleryKind === 'shared' || colors.length < 2) return true;

    var matches = galleryMatchedColors(element, colors, aliases);

    if (!matches.length) {
      /* Unknown colour-owned media stays hidden rather than risking a
         mixed-colour gallery. A selected variant's featured image is allowed
         separately by applyGalleryColorFilter().

         Orientation is the opposite case. A room shot that names no side is
         still the same sofa in either hand, and most sectional lifestyle files
         name no side at all, so hiding them would strip the gallery back to
         studio cutouts. Unattributed media stays visible on both sides. */
      return mode === 'orientation';
    }

    return matches.indexOf(selectedColor) !== -1;
  }

  function updateVisibleGalleryLabels(visibleImages) {
    var total = visibleImages.length;
    visibleImages.forEach(function (img, position) {
      var index = img.getAttribute('data-index');
      qsa('[data-thumbnail-btn][data-index="' + index + '"]').forEach(function (thumb) {
        // Both thumbnail containers are flex layouts, so order mirrors the
        // same sequence used by swipe, progress, and the main gallery.
        thumb.style.order = String(position);
        thumb.setAttribute(
          'aria-label',
          'View image ' + (position + 1) + ' of ' + total
        );
      });
    });
  }

  /* FABRIC AS A SECOND AXIS.

     Mirae and Mabel sell Colour (the wood) crossed with Fabric, so Walnut alone
     names three different chairs. The gallery already narrows by orientation and
     by size; fabric was simply never one of the axes, so choosing Almond Cream
     left every Walnut photograph on screen and the shopper saw three fabrics at
     once. Same shape as getFilterableSizeOption, same guard, same fallbacks. */
  function getFabricOption() {
    var found = null;
    if (!state.productData || !state.productData.options_with_values) return found;
    state.productData.options_with_values.forEach(function (opt) {
      var name = normalizeGalleryText(opt.name);
      if (name.indexOf('fabric') !== -1 || name.indexOf('upholstery') !== -1 || name.indexOf('material') !== -1) {
        found = opt;
      }
    });
    return found;
  }

  function galleryMatchedFabric(element, fabrics) {
    var normalized = fabrics.map(normalizeGalleryText);
    var signature = galleryMediaSignature(element);
    var matches = normalized.filter(function (f) {
      return galleryTextHas(signature, f);
    });
    if (!matches.length) return '';
    /* Prefer the most specific label, the same tie-break size uses, so a value
       that contains another ("Cream" inside "Almond Cream") cannot win. */
    matches.sort(function (a, b) {
      var wordDifference = b.split(' ').length - a.split(' ').length;
      return wordDifference || b.length - a.length;
    });
    return matches[0];
  }

  function getFilterableFabricOption() {
    var option = getFabricOption();
    if (!option || !option.values || option.values.length < 2) return null;

    /* Only filter when EVERY fabric is nameable from at least one image. If one
       fabric has no photograph that says so, filtering would empty the gallery
       for it, which is worse than the mixed gallery it shows today. */
    var images = Array.prototype.slice.call(qsa('[data-main-image]'));
    if (!images.length) return null;

    var normalized = option.values.map(normalizeGalleryText);
    var everyValueIsNamed = normalized.every(function (value) {
      return images.some(function (img) {
        return galleryMatchedFabric(img, option.values) === value;
      });
    });

    return everyValueIsNamed ? option : null;
  }

  function galleryFabricIsVisible(element, selectedFabric, fabrics) {
    if (!selectedFabric || fabrics.length < 2) return true;
    var matched = galleryMatchedFabric(element, fabrics);
    /* Names no fabric: shared across them, so keep it. */
    if (!matched) return true;
    return matched === selectedFabric;
  }

  function applyGalleryColorFilter(colorValue) {
    var filter = getGalleryFilterOption();
    var option = filter.option;
    var mainImages = Array.prototype.slice.call(qsa('[data-main-image]'));
    if (!mainImages.length) return;

    var colors = galleryColorValues();
    var selectedColor = normalizeGalleryText(colorValue);
    var colorPosition = option ? 'option' + option.position : null;
    var aliases = option ? buildGalleryColorAliases(colors, colorPosition) : {};
    var selectedFeaturedKeys = {};

    var orientationOption = getSecondaryOrientationOption();
    var orientations = galleryOrientationValues();
    var orientationPosition = orientationOption
      ? 'option' + orientationOption.position
      : null;
    var selectedOrientation = orientationPosition
      ? normalizeGalleryText(state.selectedOptions[orientationPosition])
      : '';

    var sizeOption = getFilterableSizeOption();
    var sizeValues = sizeOption && sizeOption.values ? sizeOption.values.slice() : [];
    var sizePosition = sizeOption ? 'option' + sizeOption.position : null;
    var selectedSize = sizePosition
      ? normalizeGalleryText(state.selectedOptions[sizePosition])
      : '';

    var fabricOption = getFilterableFabricOption();
    var fabricValues = fabricOption && fabricOption.values ? fabricOption.values.slice() : [];
    var fabricPosition = fabricOption ? 'option' + fabricOption.position : null;
    var selectedFabric = fabricPosition
      ? normalizeGalleryText(state.selectedOptions[fabricPosition])
      : '';

    if (colorPosition) {
      (state.productData.variants || []).forEach(function (variant) {
        if (normalizeGalleryText(variant[colorPosition]) !== selectedColor) return;
        /* Narrowed to the exact variant on show. Matching on colour alone put
           Right Facing's featured photograph on the forced list while Left
           Facing was selected, and being forced skips every other check, so it
           reappeared no matter what the side pass decided. */
        if (
          orientationPosition &&
          selectedOrientation &&
          normalizeGalleryText(variant[orientationPosition]) !== selectedOrientation
        ) {
          return;
        }
        /* Same trap one axis over. Cydra's Caramel Oak sells in 71 and 94 inch,
           so matching on colour alone forced BOTH featured photographs onto the
           list and the 94 inch front reappeared while 71 was selected. */
        if (
          sizePosition &&
          selectedSize &&
          normalizeGalleryText(variant[sizePosition]) !== selectedSize
        ) {
          return;
        }
        /* Same trap again on the fabric axis. Mabel's Walnut sells in three
           fabrics, so matching on colour alone forced all three featured
           photographs onto the list and being forced skips every other check. */
        if (
          fabricPosition &&
          selectedFabric &&
          normalizeGalleryText(variant[fabricPosition]) !== selectedFabric
        ) {
          return;
        }
        if (variant.featured_image) {
          selectedFeaturedKeys[galleryImageKey(variant.featured_image)] = true;
        }
      });
    }

    mainImages.forEach(function (img) {
      var imageKey = galleryImageKey(img.getAttribute('data-src'));
      var forcedSelectedFeature = !!selectedFeaturedKeys[imageKey];
      var visible =
        (!option ||
          !selectedColor ||
          forcedSelectedFeature ||
          galleryMediaIsVisible(img, selectedColor, colors, aliases, filter.mode)) &&
        (forcedSelectedFeature ||
          galleryOrientationIsVisible(img, selectedOrientation, orientations)) &&
        (forcedSelectedFeature ||
          gallerySizeIsVisible(img, selectedSize, sizeValues)) &&
        (forcedSelectedFeature ||
          galleryFabricIsVisible(img, selectedFabric, fabricValues));

      img.hidden = !visible;
      var index = img.getAttribute('data-index');
      qsa('[data-thumbnail-btn][data-index="' + index + '"]').forEach(function (thumb) {
        thumb.hidden = !visible;
      });
    });

    var visibleImages = getVisibleMainImages();

    /* A valid Shopify colour variant should always have a featured image. Keep
       a single-image fallback so broken catalogue metadata never leaves an
       empty gallery, while still avoiding a full mixed-colour fallback. */
    if (!visibleImages.length) {
      mainImages[0].hidden = false;
      qsa('[data-thumbnail-btn][data-index="' + mainImages[0].dataset.index + '"]')
        .forEach(function (thumb) { thumb.hidden = false; });
      visibleImages = [mainImages[0]];
    }

    updateVisibleGalleryLabels(visibleImages);

    // A colour change begins at the first image in the agreed sequence.
    // Size changes never call this function, so they preserve the exact image
    // the shopper is already viewing.
    setActiveImage(parseInt(visibleImages[0].dataset.index, 10));
  }

  function syncGalleryForSelectedColor() {
    var option = getGalleryFilterOption().option;
    if (!option) {
      applyGalleryColorFilter('');
      return;
    }

    applyGalleryColorFilter(
      state.selectedOptions['option' + option.position]
    );
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function hydrateZoomImage(img) {
    if (!img || img.getAttribute('data-zoom-hydrated') === '1') return;
    var zoomSrc = img.getAttribute('data-zoom-src');
    if (!zoomSrc) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', zoomSrc);
    img.setAttribute('data-zoom-hydrated', '1');
  }

  function syncGalleryImages() {
    var mainImages = qsa('[data-main-image]');
    var visibleImages = getVisibleMainImages();
    var activePosition = visibleImages.findIndex(function (img) {
      return parseInt(img.dataset.index, 10) === state.selectedImage;
    });

    mainImages.forEach(function (img) {
      var visiblePosition = visibleImages.indexOf(img);
      var isActive = !img.hidden && visiblePosition === activePosition;
      var shouldShow = isActive;

      img.style.setProperty(
        '--gallery-position',
        String(visiblePosition < 0 ? 0 : visiblePosition - activePosition)
      );
      img.classList.toggle('opacity-100', shouldShow);
      img.classList.toggle('opacity-0', !shouldShow);
      img.style.pointerEvents = isActive ? 'auto' : 'none';
      img.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (shouldShow) hydrateMain(img);
    });
  }

  function updateGalleryProgress() {
    var progress = qs('[data-gallery-progress]');
    if (!progress) return;

    var visibleImages = getVisibleMainImages();
    var total = visibleImages.length;
    var visibleIndex = visibleImages.findIndex(function (img) {
      return parseInt(img.dataset.index, 10) === state.selectedImage;
    });
    visibleIndex = Math.max(0, visibleIndex);

    progress.hidden = total < 2;
    progress.style.setProperty('--pdp-gallery-count', String(Math.max(1, total)));
    progress.style.setProperty('--pdp-gallery-index', String(visibleIndex));
    progress.setAttribute('aria-valuemax', String(Math.max(1, total)));
    progress.setAttribute('aria-valuenow', String(visibleIndex + 1));
    progress.setAttribute(
      'aria-label',
      'Product image ' + (visibleIndex + 1) + ' of ' + total
    );
  }

  function updatePanVisuals() {
    var wrap = getZoomWrap();
    if (!wrap) return;

    wrap.style.setProperty('--pdp-pan-x', (state.zoomPanX * 100).toFixed(2) + '%');
    wrap.style.setProperty('--pdp-pan-y', (state.zoomPanY * 100).toFixed(2) + '%');

  }

  function updateZoomUi(announce) {
    var wrap = getZoomWrap();
    if (!wrap) return;

    var isZoomed = state.zoomLevel > 1;
    var mobile = isMobileGallery();
    var badge = qs('[data-zoom-level-badge]', wrap);
    var reset = qs('[data-zoom-reset]', wrap);
    var status = qs('[data-zoom-status]', wrap);

    wrap.dataset.zoomLevel = String(state.zoomLevel);
    wrap.style.setProperty('--pdp-zoom-level', String(state.zoomLevel));
    wrap.classList.toggle('is-zoomed', isZoomed);
    wrap.setAttribute(
      'aria-label',
      isZoomed
        ? 'Product image zoomed to 2 times. ' + (mobile ? 'Drag to move around the image. Double tap to reset.' : 'Move the pointer to inspect the image. Click to reset zoom.')
        : 'Product image. ' + (document.querySelector('[data-photo-viewer]')
          ? (mobile ? 'Swipe for another image or tap to open a larger view.' : 'Click to open a larger view.')
          : (mobile ? 'Swipe for another image or double tap to zoom.' : 'Click to zoom.'))
    );

    if (badge) {
      badge.hidden = !isZoomed;
      badge.textContent = state.zoomLevel + '×';
    }
    if (reset) reset.hidden = !(mobile && isZoomed);

    updatePanVisuals();
    syncGalleryImages();

    if (announce && status) {
      status.textContent = isZoomed
        ? 'Image zoomed to 2 times.'
        : 'Image zoom reset.';
    }
  }

  function resetImageZoom(announce) {
    state.zoomLevel = 1;
    state.zoomPanX = 0.5;
    state.zoomPanY = 0.5;
    updateZoomUi(announce !== false);
  }

  function advanceImageZoom() {
    state.zoomLevel = state.zoomLevel === 1 ? 2 : 1;
    state.zoomPanX = 0.5;
    state.zoomPanY = 0.5;
    if (state.zoomLevel === 2) hydrateZoomImage(getActiveMainImage());
    updateZoomUi(true);
  }

  function setActiveImage(idx) {
    var mainImages = qsa('[data-main-image]');
    var visibleImages = getVisibleMainImages();
    if (!mainImages.length || !visibleImages.length) return;

    idx = parseInt(idx, 10);
    var requested = qs('[data-main-image][data-index="' + idx + '"]');
    if (!requested || requested.hidden) {
      idx = parseInt(visibleImages[0].dataset.index, 10);
    }
    state.selectedImage = idx;
    updateGalleryProgress();
    state.zoomLevel = 1;
    state.zoomPanX = 0.5;
    state.zoomPanY = 0.5;
    updateZoomUi(false);

    var thumbBtns = qsa('[data-thumbnail-btn]');
    thumbBtns.forEach(function (btn) {
      var isActive = parseInt(btn.dataset.index, 10) === idx;
      btn.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    var activeMobileThumb = qs('[data-thumbnail-row] [data-thumbnail-btn][data-index="' + idx + '"]');
    if (activeMobileThumb && isMobileGallery()) {
      activeMobileThumb.scrollIntoView({ block: 'nearest', inline: 'center' });
    }

    /* A thumbnail tap, a colour change or a keyboard step chooses an image
       without touching the rail, so the rail is brought to it. When the
       shopper scrolled there themselves the rail is already in place and
       railScrollToIndex returns without doing anything. */
    if (isMobileGallery()) {
      hydrateAround(idx);
      if (!railSyncing) railScrollToIndex(idx, true);
    }
  }

  function moveGalleryImage(direction) {
    var visibleImages = getVisibleMainImages();
    if (!visibleImages.length) return;

    var currentPosition = visibleImages.findIndex(function (img) {
      return parseInt(img.dataset.index, 10) === state.selectedImage;
    });
    currentPosition = Math.max(0, currentPosition);

    var nextPosition = clamp(
      currentPosition + direction,
      0,
      visibleImages.length - 1
    );
    if (nextPosition === currentPosition) return;

    setActiveImage(parseInt(visibleImages[nextPosition].dataset.index, 10));
  }

  function bindImageZoom() {
    var wrap = getZoomWrap();
    if (!wrap) return;

    var cursor = qs('[data-zoom-cursor]', wrap);
    var reset = qs('[data-zoom-reset]', wrap);
    var pointer = {
      id: null,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0
    };
    var lastTapTime = 0;
    var lastRailScrollAt = 0;
    // A tap that only stops a page fling is not an open either.
    window.addEventListener('scroll', function () { lastRailScrollAt = Date.now(); }, { passive: true });
    var lastTapX = 0;
    var lastTapY = 0;
    var lastWasMobile = isMobileGallery();

    function positionDesktopCursor(event) {
      if (isMobileGallery() || !cursor) return;
      if (event.target.closest('button')) {
        cursor.classList.remove('is-visible');
        return;
      }

      var rect = wrap.getBoundingClientRect();
      var x = clamp(event.clientX - rect.left, 0, rect.width);
      var y = clamp(event.clientY - rect.top, 0, rect.height);
      cursor.style.left = x + 'px';
      cursor.style.top = y + 'px';
      cursor.classList.add('is-visible');

      if (state.zoomLevel > 1) {
        state.zoomPanX = rect.width ? x / rect.width : 0.5;
        state.zoomPanY = rect.height ? y / rect.height : 0.5;
        updatePanVisuals();
      }
    }

    /* ── The rail reads back ───────────────────────────────────────────
       Scroll position is the truth on mobile: whatever slide the rail comes
       to rest under decides the active image, and with it the progress bar
       and the highlighted thumbnail. Guarded against its own tail, because
       setActiveImage moves the rail and that would arrive here as a fresh
       scroll and start the two chasing each other. */
    var railTicking = false;
    wrap.addEventListener('scroll', function () {
      lastRailScrollAt = Date.now();
      if (!railActive() || railSyncing || railTicking) return;
      railTicking = true;
      requestAnimationFrame(function () {
        railTicking = false;
        var idx = railIndexFromScroll();
        if (idx === null) return;
        hydrateAround(idx);
        if (idx === state.selectedImage) return;
        railSyncing = true;                 // setActiveImage must not scroll back
        setActiveImage(idx);
        railSyncing = false;
      });
    }, { passive: true });

    /* Leaving zoom hands the gesture back to the rail, which may have been
       locked away from the slide the shopper is actually looking at. */
    wrap.addEventListener('transitionend', function () {
      if (railActive() && !railSyncing) railScrollToIndex(state.selectedImage, false);
    });

    wrap.addEventListener('pointerdown', function (event) {
      if (event.target.closest('button')) return;

      // Clicking the desktop image activates keyboard gallery navigation.
      if (!isMobileGallery() && document.activeElement !== wrap) {
        try {
          wrap.focus({ preventScroll: true });
        } catch (e) {
          wrap.focus();
        }
      }

      pointer.id = event.pointerId;
      pointer.startX = event.clientX;
      pointer.startY = event.clientY;
      pointer.lastX = event.clientX;
      pointer.lastY = event.clientY;

      if (isMobileGallery()) {
        if (wrap.setPointerCapture) wrap.setPointerCapture(event.pointerId);
        if (state.zoomLevel > 1) event.preventDefault();
      }
    });

    wrap.addEventListener('pointermove', function (event) {
      if (!isMobileGallery()) {
        positionDesktopCursor(event);
        return;
      }

      if (pointer.id !== event.pointerId || state.zoomLevel === 1) return;

      var rect = wrap.getBoundingClientRect();
      var dx = event.clientX - pointer.lastX;
      var dy = event.clientY - pointer.lastY;
      var divisorX = Math.max(1, rect.width * (state.zoomLevel - 1));
      var divisorY = Math.max(1, rect.height * (state.zoomLevel - 1));

      state.zoomPanX = clamp(state.zoomPanX - (dx / divisorX), 0, 1);
      state.zoomPanY = clamp(state.zoomPanY - (dy / divisorY), 0, 1);
      pointer.lastX = event.clientX;
      pointer.lastY = event.clientY;
      updatePanVisuals();
      event.preventDefault();
    });

    wrap.addEventListener('pointerup', function (event) {
      if (pointer.id !== event.pointerId) return;

      var dx = event.clientX - pointer.startX;
      var dy = event.clientY - pointer.startY;
      var distance = Math.sqrt((dx * dx) + (dy * dy));

      if (isMobileGallery()) {
        /* The rail is the horizontal gesture now. Leaving this in would mean
           the finger and the script both moving the gallery, one by a scroll
           and one by a jump, from the same swipe. */
        if (state.zoomLevel === 1 && !railActive() && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          moveGalleryImage(dx < 0 ? 1 : -1);
          lastTapTime = 0;
        } else if (distance < 14 && state.zoomLevel === 1 &&
                   Date.now() - lastRailScrollAt > 180 && openPhotoViewer()) {
          /* One tap opens the viewer. A tap that only stopped a rail still
             gliding from a swipe is not an open, hence the scroll guard. */
          lastTapTime = 0;
        } else if (distance < 14) {
          var now = Date.now();
          var tapDistance = Math.sqrt(
            Math.pow(event.clientX - lastTapX, 2) +
            Math.pow(event.clientY - lastTapY, 2)
          );

          if (now - lastTapTime < 320 && tapDistance < 28) {
            advanceImageZoom();
            lastTapTime = 0;
            event.preventDefault();
          } else {
            lastTapTime = now;
            lastTapX = event.clientX;
            lastTapY = event.clientY;
          }
        }
      }

      if (wrap.hasPointerCapture && wrap.hasPointerCapture(event.pointerId)) {
        wrap.releasePointerCapture(event.pointerId);
      }
      pointer.id = null;
    });

    wrap.addEventListener('pointercancel', function (event) {
      if (pointer.id === event.pointerId) pointer.id = null;
    });

    wrap.addEventListener('pointerleave', function () {
      if (cursor) cursor.classList.remove('is-visible');
    });

    wrap.addEventListener('click', function (event) {
      // The event's own path, not the live DOM: the wishlist heart swaps its
      // SVG on click, so event.target can already be detached from the button.
      var path = event.composedPath ? event.composedPath() : [event.target];
      var onButton = path.some(function (n) { return n && n.nodeType === 1 && n.tagName === 'BUTTON'; });
      if (isMobileGallery() || onButton) return;
      if (!openPhotoViewer()) advanceImageZoom();
    });

    wrap.addEventListener('keydown', function (event) {
      // Keys aimed at a control inside the gallery (the wishlist heart) belong
      // to that control, not to the gallery.
      if (event.target !== wrap) return;
      if (!isMobileGallery()) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          moveGalleryImage(-1);
          return;
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          moveGalleryImage(1);
          return;
        }
      }

      if (event.key === 'Escape') {
        resetImageZoom();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!openPhotoViewer()) advanceImageZoom();
      }
    });

    if (reset) {
      reset.addEventListener('click', function () {
        resetImageZoom();
      });
    }

    window.addEventListener('resize', function () {
      var mobile = isMobileGallery();
      if (mobile !== lastWasMobile) {
        lastWasMobile = mobile;
        resetImageZoom(false);
      } else {
        updateZoomUi(false);
      }
    }, { passive: true });

    setActiveImage(state.selectedImage);
  }

  /* ── COLOR SWATCHES ── */
  /* Which option position is the colour (works for 'Color'/'Colour', any order) */
  function getColorPosition() {
    var option = getColorOption();
    return option ? 'option' + option.position : 'option1';
  }

  /* Which option position is the size (Size/Seater), or null if the product has none */
  function getSizePosition() {
    var option = getSizeOption();
    return option ? 'option' + option.position : null;
  }

  /* Images are per-COLOUR (not per-size). If the chosen colour has no variant at
     the current size, move to a size that DOES offer it (prefer an in-stock one)
     so the picker never lands on a dead combo and the image/price/ATC always
     follow the colour. Works for any product with an uneven colour x size grid. */
  function reconcileSizeForColor(colorValue) {
    if (!state.productData) return;
    var colorPos = getColorPosition();
    var sizePos = getSizePosition();
    if (!sizePos) return;
    var curSize = state.selectedOptions[sizePos];
    var exact = state.productData.variants.find(function (v) {
      return v[colorPos] === colorValue && v[sizePos] === curSize;
    });
    if (exact) return; // current size already offers this colour
    var candidates = state.productData.variants.filter(function (v) { return v[colorPos] === colorValue; });
    if (!candidates.length) return;
    var best = candidates.find(function (v) { return v.available; }) || candidates[0];
    if (best[sizePos] && best[sizePos] !== curSize) setSizeUI(best[sizePos]);
  }

  function bindColorSwatches() {
    var swatches = qsa('[data-color-swatch]');
    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.colorValue;
        if (state.selectedOptions[getColorPosition()] === value) return;
        animateVariantControl(btn);
        selectColor(value);
      });
      btn.addEventListener('mouseenter', function () {
        var label = qs('[data-color-label]');
        if (label) label.textContent = btn.dataset.colorValue;
      });
      btn.addEventListener('mouseleave', function () {
        var label = qs('[data-color-label]');
        if (label) label.textContent = state.selectedOptions[getColorPosition()] || '';
      });
    });
  }

  function selectColor(value) {
    state.selectedOptions[getColorPosition()] = value;
    var label = qs('[data-color-label]');
    if (label) label.textContent = value;

    /* Selected state is aria-checked, painted by CSS. The swatch is a labelled
       pill now rather than a bare circle, so there is no tick to inject and no
       ring classes to splice in and out of a className string. */
    qsa('[data-color-swatch]').forEach(function (btn) {
      btn.setAttribute('aria-checked', btn.dataset.colorValue === value ? 'true' : 'false');
    });

    reconcileSizeForColor(value);
    applySizeAvailability(value);

    /* Keep all variant data updates immediate, then transition only the visual
       gallery swap. This leaves price, availability, URL, preorder and selected
       option logic exactly as before while avoiding a hard image cut. */
    updateVariant({ syncGallery: false });
    transitionGalleryForVariantChange(syncGalleryForSelectedColor);
  }

  /* ── SIZE SELECTOR ── */
  function bindSizeSelector() {
    var btns = qsa('[data-size-btn]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.sizeValue;
        if (state.selectedOptions[getSizePosition() || 'option2'] === value) return;
        animateVariantControl(btn);
        selectSize(value);
      });
    });
  }

  function setSizeUI(value) {
    var sizePosition = getSizePosition() || 'option2';
    state.selectedOptions[sizePosition] = value;

    /* aria-checked is the selected state: the stylesheet paints from it and the
       radiogroup reads from it. This used to restate two whole Tailwind class
       strings instead, which left aria-checked stale on every row after the
       first click, so assistive tech kept announcing the original size. */
    qsa('[data-size-btn]').forEach(function (btn) {
      btn.setAttribute('aria-checked', btn.dataset.sizeValue === value ? 'true' : 'false');
    });
  }

  function selectSize(value) {
    setSizeUI(value);
    updateVariant();
    /* Historically a size change deliberately left the gallery alone so the
       shopper kept the photo they were looking at. That is still right when
       size does not filter, but where it does the visible set has just changed
       and has to be recomputed. */
    if (getFilterableSizeOption()) syncGalleryForSelectedColor();
  }

  /* The size rows are rendered from every value the option has, so a colour that
     is only made in one size still offered the others (Cashew Cream is a
     3 Seater only, but the 2 Seater row showed anyway with no variant behind it).
     Hide the sizes this colour is not made in. This is about existence, not
     stock, so a real but sold out size still shows. reconcileSizeForColor
     already moves the selection to a real one, so nothing can be left pointing
     at a combination that does not exist. */
  function applySizeAvailability(colour) {
    if (!state.productData || !colour) return;
    /* getColorPosition() falls back to 'option1' even when the product has no
       colour option at all, so on a size-only product colorPos and sizePos are
       the SAME option and the filter below matches nothing but the size already
       selected. Blanco Black, Blanco White, Maeve and Box Spring sell one option
       (Size) and would show a single row. Only filter sizes by colour when there
       is genuinely a colour to filter by. */
    var colorOption = getColorOption();
    var sizePos = getSizePosition();
    if (!colorOption || !sizePos) return;
    var colorPos = 'option' + colorOption.position;
    if (colorPos === sizePos) return;
    var rows = qsa('[data-size-btn]');
    if (!rows.length) return;
    var shown = 0;
    rows.forEach(function (btn) {
      var size = btn.dataset.sizeValue;
      var matchingVariants = state.productData.variants.filter(function (v) {
        return v[colorPos] === colour && v[sizePos] === size;
      });

      /* Liquid renders these prices for the colour present at page load. Refresh
         them from the selected colour's exact variant so a later colour switch
         cannot leave a stale size price beside the newly selected variant. When
         a product has a third option, prefer the variant matching that current
         selection before falling back to another real colour-and-size variant. */
      var rowVariant = matchingVariants.find(function (v) {
        return ['option1', 'option2', 'option3'].every(function (optionPosition) {
          var selectedValue = state.selectedOptions[optionPosition];
          return (
            optionPosition === colorPos ||
            optionPosition === sizePos ||
            !selectedValue ||
            v[optionPosition] === selectedValue
          );
        });
      }) || matchingVariants.find(function (v) {
        return v.available;
      }) || matchingVariants[0];

      var exists = !!rowVariant;
      btn.style.display = exists ? '' : 'none';
      if (exists) {
        var formattedPrice = formatMoney(rowVariant.price);
        var priceEl = qs('.size-row-price', btn);
        if (priceEl) priceEl.textContent = formattedPrice;
        btn.dataset.variantPrice = formattedPrice;
        shown++;
      }
    });
    /* Nothing matched, which is a data gap rather than a real single-size
       colour. Show everything again so the shopper is never left with an empty
       picker. */
    if (shown === 0) rows.forEach(function (btn) { btn.style.display = ''; });
  }

  /* ── OPTION 3 SELECTOR (Material, Finish, etc.) ── */
  function bindOption3Selector() {
    var btns = qsa('[data-option3-btn]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.option3Value;
        state.selectedOptions['option3'] = value;

        btns.forEach(function (b) {
          if (b.dataset.option3Value === value) {
            b.className = 'px-5 py-2.5 min-h-[44px] rounded-lg text-[13px] transition-all duration-200 cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 touch-manipulation border-2 border-foreground bg-foreground/[0.03] font-medium';
          } else {
            b.className = 'px-5 py-2.5 min-h-[44px] rounded-lg text-[13px] transition-all duration-200 cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 touch-manipulation border border-border hover:border-foreground/30';
          }
        });

        updateVariant();
      });
    });
  }

  /* ── GENERIC OPTION SELECTOR ──
        Any option the named selectors do not claim. The template emits the
        option's position (1, 2 or 3) so we know which slot to write, and the
        selected look is CSS keyed on aria-checked rather than a class string
        duplicated here. */
  function bindGenericOptionSelectors() {
    /* Fabric arrives here, because no named selector claims it, and this
       handler only ever called updateVariant(), which deliberately leaves the
       gallery alone so that changing size does not yank the photo the shopper
       is reading. That is right for an option which does not filter, and wrong
       for one which does: on Mirae and Mabel, choosing a fabric changed the
       price and the URL while the images sat still.

       Colour, orientation and size each already refresh the gallery after the
       variant update. This does the same for any generic option that filters,
       which today means Fabric. */
    function genericOwnsGallery(position) {
      var fabric = getFilterableFabricOption();
      return !!(fabric && String(fabric.position) === String(position));
    }

    function afterGenericChange(position) {
      if (genericOwnsGallery(position)) {
        updateVariant({ syncGallery: false });
        transitionGalleryForVariantChange(syncGalleryForSelectedColor);
      } else {
        updateVariant();
      }
    }

    /* Long option lists render as a dropdown instead of a row of pills. */
    qsa('[data-generic-option-select]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var pos = sel.dataset.genericOptionPosition;
        state.selectedOptions['option' + pos] = sel.value;
        afterGenericChange(pos);
      });
    });

    var btns = qsa('[data-generic-option-btn]');
    if (!btns.length) return;

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var pos = btn.dataset.genericOptionPosition;
        var value = btn.dataset.genericOptionValue;
        state.selectedOptions['option' + pos] = value;

        btns.forEach(function (b) {
          if (b.dataset.genericOptionPosition !== pos) return;
          b.setAttribute('aria-checked', b.dataset.genericOptionValue === value ? 'true' : 'false');
        });

        afterGenericChange(pos);
      });
    });
  }

  /* ── ORIENTATION SELECTOR (Configuration / Directional / Chaise / Facing)
        Works at any option position (1, 2, or 3). The Liquid template emits
        data-orientation-position="1|2|3" so we know which option to update. */
  function bindOrientationSelector() {
    var btns = qsa('[data-orientation-btn]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.orientationValue;
        var position = parseInt(btn.dataset.orientationPosition, 10) || 1;
        state.selectedOptions['option' + position] = value;

        /* Keep one fixed class list. CSS paints the selected state from
           aria-checked, so clicking cannot restore the old 88px icon-card size. */
        btns.forEach(function (b) {
          var isSelected = b.dataset.orientationValue === value;
          b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        /* updateVariant() leaves the gallery alone unless it is asked not to,
           because size and every option that does not own the gallery must
           preserve the exact photo the shopper is looking at. A bare
           updateVariant() therefore updated price, availability and the URL
           while the images stayed on whatever the page first rendered: picking
           Right Facing left all the left-facing photos on screen.

           When orientation is the option the gallery filters on, it does own
           the gallery and has to move it, the same way the colour selector
           does. When something else owns it, the old behaviour is right. */
        var filterOption = getGalleryFilterOption().option;
        var secondaryOrientation = getSecondaryOrientationOption();
        var ownsGallery =
          (filterOption && filterOption.position === position) ||
          (secondaryOrientation && secondaryOrientation.position === position);
        if (ownsGallery) {
          updateVariant({ syncGallery: false });
          transitionGalleryForVariantChange(syncGalleryForSelectedColor);
        } else {
          updateVariant();
        }
      });
    });
  }

  /* ── LOYAL TRADE LINE: type-on label + count-up price + savings fade ──
     Reads data-loyal-label/data-loyal-percent from the .loyal-line element,
     types the label out one character at a time, then counts the price down
     from regular -> regular*(1-percent), then fades in the savings amount. */
  function initLoyalLine() {
    var line = qs('[data-loyal-line]');
    if (!line) return;
    var label = line.dataset.loyalLabel || 'Loyal Trade';
    var typedEl = line.querySelector('[data-loyal-typed]');
    var priceEl = line.querySelector('[data-loyal-price]');
    var savingsEl = line.querySelector('[data-loyal-savings]');
    if (!typedEl || !priceEl) return;

    var regCents = parseInt(priceEl.dataset.loyalPriceCents, 10) * (100 / (100 - parseInt(line.dataset.loyalPercent, 10)));
    var loyalCents = parseInt(priceEl.dataset.loyalPriceCents, 10);
    var serverFormatted = priceEl.textContent.trim();
    var savingsFormatted = savingsEl ? savingsEl.textContent.trim() : '';

    /* Build cursor + initially empty type slot */
    typedEl.innerHTML = '<span class="loyal-typed-text"></span><span class="loyal-cursor" aria-hidden="true">|</span>';
    var typedText = typedEl.querySelector('.loyal-typed-text');
    var cursor = typedEl.querySelector('.loyal-cursor');

    function startWhenInView() {
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          typeOn();
        }
      }, { threshold: 0.3 });
      observer.observe(line);
    }

    function typeOn() {
      var i = 0;
      var step = setInterval(function () {
        i++;
        typedText.textContent = label.slice(0, i);
        if (i >= label.length) {
          clearInterval(step);
          if (cursor) {
            setTimeout(function () { cursor.style.display = 'none'; }, 800);
          }
          setTimeout(countPrice, 220);
        }
      }, 60);
    }

    function countPrice() {
      priceEl.style.opacity = '1';
      var duration = 900;
      var t0 = performance.now();
      function tick(now) {
        var t = Math.min(1, (now - t0) / duration);
        var eased = 1 - Math.pow(1 - t, 3);
        var cur = Math.round(regCents - (regCents - loyalCents) * eased);
        priceEl.textContent = formatMoney(cur);
        if (t < 1) requestAnimationFrame(tick);
        else {
          priceEl.textContent = serverFormatted;
          if (savingsEl) {
            savingsEl.style.transition = 'opacity 0.5s cubic-bezier(0.22,1,0.36,1)';
            savingsEl.style.opacity = '1';
          }
        }
      }
      requestAnimationFrame(tick);
    }

    startWhenInView();
  }


  /* The button's own label, so a custom "add_to_cart_text" survives a variant
     change. The price is deliberately not appended: it is already stated above
     the picker and on every size row. */
  /* A variant selling with no stock behind it is a pre-order, and the button
     has to say so on its own. The pre-order app hides this button and injects
     its own; when its script does not finish, whatever is left standing is the
     only thing the customer sees, so it must not read "Add to Cart" for
     something that ships in months. Both labels come off the button's own data
     attributes, so section settings still win over anything hard-coded. */
  function cartLabel(variant) {
    var btn = qs('[data-add-to-cart]');
    if (variant && variant.preorder) {
      return (btn && btn.getAttribute('data-preorder-label')) || 'Preorder';
    }
    return (btn && btn.getAttribute('data-cart-label')) || 'Add to Cart';
  }

  /* Keep the delivery estimator pointed at the variant the shopper actually
     selected. The estimator owns its visual reset so this product script only
     publishes the new variant facts. */
  function syncDeliveryEstimateVariant(variant) {
    if (!variant) return;
    Array.prototype.forEach.call(qsa('[data-delivery-estimate]'), function (estimator) {
      estimator.dataset.variantId = 'gid://shopify/ProductVariant/' + variant.id;
      estimator.dataset.priceCents = variant.price;
      estimator.dataset.preorder = variant.preorder ? 'true' : 'false';
      estimator.dataset.available = variant.available ? 'true' : 'false';
      estimator.dispatchEvent(new CustomEvent('delivery-estimate:variant-change', {
        detail: {
          variantId: variant.id,
          priceCents: variant.price,
          preorder: !!variant.preorder,
          available: !!variant.available
        }
      }));
    });
  }

  /* Announce the selected variant to the rest of the page.

     Sections rendered below the hero (the bundle, most obviously) get their
     variant from Liquid at page load and never hear about a switch, because the
     picker uses history.replaceState and never reloads. Before this event the
     bundle kept posting whichever variant Liquid happened to render first: pick
     Toffee Cream on the Lunel bed and "Add Selected to Cart" still added Cream
     Marshmallow.

     The colour is published separately from the variant because listeners key
     off it directly. getColorPosition() falls back to 'option1' when a product
     has no colour option at all, which would hand out a size as a colour, so
     this reads getColorOption() and reports null instead. */
  function publishVariantChange(variant) {
    if (!variant) return;
    var colorOption = getColorOption();
    var colorValue = null;
    if (colorOption) {
      colorValue = state.selectedOptions['option' + colorOption.position] || null;
    }
    var detail = {
      productId: state.productData ? state.productData.id : null,
      variantId: variant.id,
      title: variant.title,
      price: variant.price,
      available: !!variant.available,
      preorder: !!variant.preorder,
      featuredImage: variant.featured_image || null,
      color: colorValue
    };
    /* Stashed as well as dispatched because load order is not on our side. Both
       scripts are deferred and deferred scripts run in document order, so this
       one (in the hero) always runs before the bundle's, which sits further down
       the page. A listener that binds after the opening variant is published
       would simply miss it. Reading the stash on init removes the race. */
    window.AykahSelectedVariant = detail;
    document.dispatchEvent(new CustomEvent('product:variant-change', { detail: detail }));
  }

  /* ── VARIANT UPDATE ── */
  function updateVariant(options) {
    if (!state.productData) return;

    /* Variants control price, availability and purchase state. The gallery is
       colour-owned, so size and every non-colour option must preserve the exact
       image the shopper is currently viewing. */
    var syncGallery = !!(options && options.syncGallery);

    var matchedVariant = state.productData.variants.find(function (v) {
      return (
        (!state.selectedOptions['option1'] || v.option1 === state.selectedOptions['option1']) &&
        (!state.selectedOptions['option2'] || v.option2 === state.selectedOptions['option2']) &&
        (!state.selectedOptions['option3'] || v.option3 === state.selectedOptions['option3'])
      );
    });

    if (matchedVariant) {
      state.variantId = matchedVariant.id;
      syncDeliveryEstimateVariant(matchedVariant);

      /* Keep the form's own variant in step. Add to Cart posts state.variantId
         through /cart/add.js so the purchase is right either way, but this
         hidden input is what a native form submit would send, and nothing was
         ever writing it: it sat on whichever variant Liquid rendered. On the
         gift card that meant the form still described the $25 card after the
         shopper had chosen $500. */
      var variantInput = qs('[data-variant-input]');
      if (variantInput && variantInput.value !== String(matchedVariant.id)) {
        variantInput.value = matchedVariant.id;

        /* Globo Pre-order reads the pre-order date off this input and re-renders
           its message on the input's `change` event. Assigning .value from JS
           does not fire `change` (only real user input does), and the URL update
           below uses replaceState, which fires no navigation event either. So
           before this dispatch the app never learned the variant had moved and
           left the date frozen on whichever variant the page first loaded with.

           On the Lumera Dining Table that meant Rich Walnut showed Honey Oak's
           "August 1-15, 2026" when the app actually has it as September 16-30.
           17 of the 94 pre-order products have per-variant dates and were all
           showing the wrong one after a switch.

           The value guard above keeps this to a real change, so nothing re-fires
           when the shopper reselects the option they already had. */
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* What this variant costs the shopper, and what to strike through beside it.

         sale_price is precomputed per variant in Liquid, because the sale runs as a
         Shopify AUTOMATIC DISCOUNT: variant.price stays at full price and the
         reduction only lands in the cart, so there is nothing on the variant itself
         to read. Doing the arithmetic server-side also keeps it identical to
         Shopify's own, which floors the discount rather than the final price. On a
         $2,499.99 sofa the two differ by a cent, and a price that is a cent off what
         the cart charges is exactly the thing that costs trust. */
      var shownPrice = matchedVariant.sale_price != null ? matchedVariant.sale_price : matchedVariant.price;
      var shownWas = null;
      if (matchedVariant.sale_price != null) {
        shownWas = matchedVariant.price;
      } else if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
        shownWas = matchedVariant.compare_at_price;
      }

      // Update price
      var priceEl = qs('[data-current-price]');
      if (priceEl) {
        priceEl.textContent = formatMoney(shownPrice);
        priceEl.setAttribute('data-current-price-cents', shownPrice);
      }

      // Keep the Affirm monthly-payment promo in sync with the selected variant
      var alaEl = qs('[data-affirm-promo]');
      if (alaEl) {
        alaEl.dataset.amount = matchedVariant.price;
        if (window.affirm && window.affirm.ui && window.affirm.ui.refresh) {
          window.affirm.ui.ready(function () { window.affirm.ui.refresh(); });
        }
      }

      /* Update the struck-through price and the saving.

         Both elements are now always present in the markup, hidden rather than
         omitted, so these lookups cannot come back null on a product whose first
         variant is not the discounted one. */
      var compareEl = qs('[data-compare-price]');
      if (compareEl) {
        if (shownWas) {
          compareEl.textContent = formatMoney(shownWas);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        }
      }

      var savingEl = qs('[data-price-saving]');
      if (savingEl) {
        if (shownWas) {
          savingEl.textContent = 'Save ' + formatMoney(shownWas - shownPrice);
          savingEl.classList.remove('hidden');
        } else {
          savingEl.textContent = '';
          savingEl.classList.add('hidden');
        }
      }

      /* "10% off at checkout" follows the variant. When only some variants are in
         the sale (ticked "On sale" in admin), switching to one that is not must hide
         the line, or the page promises a discount checkout will not give.
         Only an explicit false hides it, so older markup without the field keeps
         today's behaviour. */
      var saleNoteEl = qs('[data-sale-note]');
      if (saleNoteEl && typeof matchedVariant.sale_note === 'boolean') {
        saleNoteEl.classList.toggle('hidden', !matchedVariant.sale_note);
      }

      /* The sticky bar carries the price too, and nothing was keeping it in step:
         it kept whatever the page first rendered, so after a size switch it could
         offer to add one size at another size's price. */
      var stickyTextEl = qs('[data-sticky-cart-text]');
      if (stickyTextEl && matchedVariant.available) {
        var stickyLabel = (stickyTextEl.textContent || '').split(' · ')[0].trim();
        if (stickyLabel && stickyLabel.toLowerCase() !== 'sold out') {
          stickyTextEl.textContent = stickyLabel + ' · ' + formatMoney(shownPrice);
        }
      }

      // Update monthly price
      var monthlyEl = qs('[data-monthly-price]');
      if (monthlyEl) monthlyEl.textContent = formatMoney(Math.ceil(matchedVariant.price / 12));

      // Update ATC button
      var atcBtn = qs('[data-add-to-cart]');
      if (atcBtn) {
        atcBtn.dataset.variantId = matchedVariant.id;
        var cartText = qs('[data-cart-text]');
        var cartIcon = qs('[data-cart-icon]');

        if (matchedVariant.available) {
          atcBtn.disabled = false;
          atcBtn.classList.remove('bg-muted', 'text-muted-foreground', 'cursor-not-allowed');
          atcBtn.classList.add('bg-foreground', 'text-white', 'hover:bg-foreground/90');
          if (cartIcon) cartIcon.classList.remove('hidden');
          if (cartText) {
            cartText.textContent = cartLabel(matchedVariant);
          }
          /* Kept in sync so the attribute never contradicts the label after a
             switch from a stocked variant to a pre-order one. */
          if (matchedVariant.preorder) {
            atcBtn.setAttribute('data-is-preorder', 'true');
          } else {
            atcBtn.removeAttribute('data-is-preorder');
          }
        } else {
          atcBtn.disabled = true;
          atcBtn.classList.remove('bg-foreground', 'text-white', 'hover:bg-foreground/90');
          atcBtn.classList.add('bg-muted', 'text-muted-foreground', 'cursor-not-allowed');
          if (cartIcon) cartIcon.classList.add('hidden');
          if (cartText) cartText.textContent = 'Sold Out';
        }
      }

      // Update URL
      var url = new URL(window.location);
      url.searchParams.set('variant', matchedVariant.id);
      window.history.replaceState({}, '', url);

      // Only an explicit colour change may rebuild and move the gallery. Size
      // changes preserve the exact filtered sequence and active image.
      if (syncGallery) {
        syncGalleryForSelectedColor();
      }

      // Published last, once price, availability, the form input and the URL
      // have all settled, so a listener reading the page sees a consistent state.
      publishVariantChange(matchedVariant);
    }
  }

  /* ── QUANTITY ──
     Purchase limits come from the product metafields, rendered onto the
     stepper as data attributes by product-hero.liquid. Chairs ship two to a
     box, so the control has to start at the minimum and move in steps rather
     than letting someone pick a quantity that cannot be fulfilled.

     Going below the minimum opens the modal instead of silently refusing.
     A stepper that just stops responding reads as broken, and the shopper is
     never told why. */
  function qtyRules() {
    var wrap = qs('[data-quantity-selector]');
    var min = parseInt(wrap && wrap.getAttribute('data-qty-min'), 10);
    var step = parseInt(wrap && wrap.getAttribute('data-qty-step'), 10);
    var max = parseInt(wrap && wrap.getAttribute('data-qty-max'), 10);
    return {
      min: isNaN(min) || min < 1 ? 1 : min,
      step: isNaN(step) || step < 1 ? 1 : step,
      max: isNaN(max) || max < 1 ? 0 : max        /* 0 means no ceiling */
    };
  }

  /* The form carries its own hidden quantity so that anything submitting it
     natively sends the right number. Globo Pre-order takes over the button on a
     pre-order product and the form really does post, and before this field
     existed Shopify defaulted that to 1: a chair that ships two to a box entered
     the cart as a single. Our own path posts state.quantity to /cart/add.js and
     never touched the form, which is why only pre-order products were wrong.
     Write both together so they can never disagree. */
  function setQuantity(value, display) {
    state.quantity = value;
    if (display) display.textContent = value;
    var input = qs('[data-quantity-input]');
    if (input) input.value = value;
  }

  function bindQuantity() {
    var minusBtn = qs('[data-quantity-minus]');
    var plusBtn = qs('[data-quantity-plus]');
    var display = qs('[data-quantity-value]');
    var rules = qtyRules();

    /* The server already rendered the minimum, so start in step with it. */
    setQuantity(rules.min, display);

    if (minusBtn) {
      minusBtn.addEventListener('click', function () {
        var next = state.quantity - rules.step;
        if (next < rules.min) {
          openQtyMinModal(rules);
          return;
        }
        setQuantity(next, display);
        updateCartButtonText();
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', function () {
        var next = state.quantity + rules.step;
        if (rules.max && next > rules.max) return;
        if (!rules.max && next > 10 * rules.step) return;
        setQuantity(next, display);
        updateCartButtonText();
      });
    }

    bindQtyMinModal(rules, display);
  }

  /* ── MINIMUM QUANTITY MODAL ── */
  function currentVariantForModal() {
    if (!state.productData) return null;
    return state.productData.variants.filter(function (v) {
      return v.id === state.variantId;
    })[0] || null;
  }

  function openQtyMinModal(rules) {
    var modal = qs('[data-qty-min-modal]');
    if (!modal) return;
    var variant = currentVariantForModal();
    var product = (state.productData && state.productData.title) || '';
    var colour = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';

    var heading = modal.querySelector('[data-qty-min-heading]');
    var body = modal.querySelector('[data-qty-min-body]');
    var vLabel = modal.querySelector('[data-qty-min-variant]');
    var pLabel = modal.querySelector('[data-qty-min-product]');
    var thumb = modal.querySelector('[data-qty-min-thumb]');

    /* The minus is blocked before the number ever drops, so the quantity is
       always sitting at the minimum when this opens. Telling someone they have
       2 selected and the minimum is 2 states a problem that is not there. Say
       why the floor exists instead. */
    var WORD = { 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 8: 'eight', 10: 'ten', 12: 'twelve' };
    var PLURAL = { 2: 'twos', 3: 'threes', 4: 'fours', 5: 'fives', 6: 'sixes', 8: 'eights', 10: 'tens', 12: 'twelves' };
    var one = WORD[rules.step] || String(rules.step);
    var many = PLURAL[rules.step] || rules.step + 's';

    if (heading) {
      heading.textContent = rules.step === 2 ? 'Sold in pairs' : 'Sold in sets of ' + rules.step;
    }
    if (body) {
      body.textContent = 'This product ships ' + one + ' to a box, so quantities go up in ' + many + '.';
    }
    if (pLabel && product) pLabel.textContent = product;
    if (vLabel) vLabel.textContent = colour;
    if (thumb && variant && variant.featured_image && variant.featured_image.src) {
      thumb.setAttribute('src', variant.featured_image.src);
    }

    modal.classList.remove('hidden');
    modal.classList.add('flex');
    /* next frame, so the transition runs rather than snapping */
    requestAnimationFrame(function () {
      var scrim = modal.querySelector('[data-qty-min-scrim]');
      var card = modal.querySelector('[data-qty-min-card]');
      if (scrim) scrim.classList.remove('opacity-0');
      if (card) card.classList.remove('opacity-0', 'translate-y-2', 'scale-[0.98]');
    });
    document.body.style.overflow = 'hidden';
  }

  function closeQtyMinModal() {
    var modal = qs('[data-qty-min-modal]');
    if (!modal) return;
    var scrim = modal.querySelector('[data-qty-min-scrim]');
    var card = modal.querySelector('[data-qty-min-card]');
    if (scrim) scrim.classList.add('opacity-0');
    if (card) card.classList.add('opacity-0', 'translate-y-2', 'scale-[0.98]');
    document.body.style.overflow = '';
    setTimeout(function () {
      modal.classList.add('hidden');
      modal.classList.remove('flex');
    }, 200);
  }

  function bindQtyMinModal(rules, display) {
    var modal = qs('[data-qty-min-modal]');
    if (!modal || modal.getAttribute('data-qty-bound') === 'true') return;
    modal.setAttribute('data-qty-bound', 'true');

    ['[data-qty-min-close]', '[data-qty-min-dismiss]', '[data-qty-min-scrim]'].forEach(function (sel) {
      var el = modal.querySelector(sel);
      if (el) el.addEventListener('click', closeQtyMinModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) closeQtyMinModal();
    });
  }

  function updateCartButtonText() {
    var cartText = qs('[data-cart-text]');
    var atcBtn = qs('[data-add-to-cart]');
    if (!cartText || !state.productData) return;

    var variant = state.productData.variants.find(function (v) {
      return v.id === state.variantId;
    });

    if (variant && variant.available) {
      cartText.textContent = cartLabel(variant);
    } else if (variant) {
      cartText.textContent = 'Sold Out';
    }
  }

  /* ── ADD TO CART ── */
  function bindAddToCart() {
    var btn = qs('[data-add-to-cart]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      if (!state.variantId) return;

      var cartIcon = qs('[data-cart-icon]');
      var checkIcon = qs('[data-check-icon]');
      var cartText = qs('[data-cart-text]');

      // Show loading state
      btn.disabled = true;
      if (cartText) cartText.textContent = 'Adding...';

      /* The drawer reads its rules from window.AykahQtyRules, which Liquid
         renders for the cart as it was at page load. A product added now is
         not in that map yet, so publish it before the drawer re-renders. */
      var addRules = qtyRules();
      if (addRules.min > 1 || addRules.step > 1 || addRules.max > 0) {
        window.AykahQtyRules = window.AykahQtyRules || {};
        if (state.productData && state.productData.id) {
          window.AykahQtyRules[String(state.productData.id)] = {
            min: addRules.min, step: addRules.step, max: addRules.max
          };
        }
      }

      var items = [{ id: state.variantId, quantity: state.quantity }];

      // Check for extended warranty
      var warrantyCheckbox = qs('[data-warranty-checkbox]');
      if (warrantyCheckbox && warrantyCheckbox.checked) {
        var warrantyId = parseInt(warrantyCheckbox.dataset.warrantyVariantId, 10);
        if (warrantyId) {
          items.push({ id: warrantyId, quantity: state.quantity });
        }
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items }),
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (data) {
              var msg = data.description || data.message || ('Error ' + res.status);
              throw new Error(msg);
            });
          }
          return res.json();
        })
        .then(function () {
          // Success UI — only show after confirmed add
          btn.classList.remove('bg-foreground', 'hover:bg-foreground/90');
          btn.classList.add('bg-green-700');
          if (cartIcon) cartIcon.classList.add('hidden');
          if (checkIcon) checkIcon.classList.remove('hidden');
          if (cartText) cartText.textContent = 'Added to Cart';
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function (err) {
          if (cartText) cartText.textContent = err.message.length < 40 ? err.message : 'Error. Try Again';
          btn.classList.remove('bg-foreground', 'hover:bg-foreground/90');
          btn.classList.add('bg-red-600');
        })
        .finally(function () {
          btn.disabled = false;
          // Reset button after 2.5s
          setTimeout(function () {
            btn.classList.remove('bg-green-700', 'bg-red-600');
            btn.classList.add('bg-foreground', 'hover:bg-foreground/90');
            if (cartIcon) cartIcon.classList.remove('hidden');
            if (checkIcon) checkIcon.classList.add('hidden');
            updateCartButtonText();
          }, 2500);
        });
    });
  }

  /* ── WISHLIST (localStorage) ── */
  function wishlistSavedHandle(saved) {
    return typeof saved === 'string'
      ? saved
      : saved && typeof saved.handle === 'string'
        ? saved.handle
        : '';
  }

  function normalizeWishlistOptions(options) {
    var source = options && typeof options === 'object' ? options : {};
    var normalized = {};
    var colour = source.colour || source.color;

    if (typeof colour === 'string' && colour.trim()) normalized.colour = colour.trim();
    ['size', 'orientation', 'option1', 'option2', 'option3'].forEach(function (key) {
      if (typeof source[key] === 'string' && source[key].trim()) {
        normalized[key] = source[key].trim();
      }
    });
    return normalized;
  }

  function normalizeWishlistSaved(saved) {
    var handle = wishlistSavedHandle(saved);
    if (!handle) return null;

    if (typeof saved === 'string') {
      return { handle: handle, variantId: null, options: {}, partial: true };
    }

    var variantId = parseInt(saved.variantId, 10) || null;
    var options = normalizeWishlistOptions(saved.options);
    return {
      handle: handle,
      variantId: variantId,
      options: options,
      partial: saved.partial === true || (!variantId && Object.keys(options).length === 0)
    };
  }

  function wishlistRecordScore(saved) {
    if (!saved) return -1;
    return (saved.variantId ? 100 : 0) + Object.keys(saved.options || {}).length * 10 + (saved.partial ? 0 : 1);
  }

  function wishlistRecordKey(saved) {
    var handle = wishlistSavedHandle(saved);
    if (!handle) return '';
    var variantId = saved && typeof saved === 'object' ? parseInt(saved.variantId, 10) || null : null;
    var isPartial = typeof saved === 'string' || !variantId || (saved && saved.partial === true);
    return handle + (isPartial ? '::partial' : '::variant:' + variantId);
  }

  function normalizeWishlist(items) {
    var normalized = [];
    var positions = Object.create(null);

    (Array.isArray(items) ? items : []).forEach(function (raw) {
      var saved = normalizeWishlistSaved(raw);
      if (!saved) return;

      var key = wishlistRecordKey(saved);
      var existingPosition = positions[key];
      if (existingPosition === undefined) {
        positions[key] = normalized.length;
        normalized.push(saved);
        return;
      }

      if (wishlistRecordScore(saved) >= wishlistRecordScore(normalized[existingPosition])) {
        normalized[existingPosition] = saved;
      }
    });

    /* A collection-card/legacy partial row is only a bridge to option choice.
       Once an exact variant exists for that product, the partial row is stale. */
    var handlesWithExactVariants = Object.create(null);
    normalized.forEach(function (saved) {
      if (saved.variantId && !saved.partial) handlesWithExactVariants[saved.handle] = true;
    });
    return normalized.filter(function (saved) {
      return !(saved.partial && handlesWithExactVariants[saved.handle]);
    });
  }

  function getWishlist() {
    try {
      var stored = JSON.parse(localStorage.getItem('aykah_wishlist') || '[]');
      var normalized = normalizeWishlist(stored);

      /* Migrate legacy rows and collapse only true duplicates of the same variant. */
      if (JSON.stringify(stored) !== JSON.stringify(normalized)) {
        localStorage.setItem('aykah_wishlist', JSON.stringify(normalized));
      }
      return normalized;
    } catch (e) {
      return [];
    }
  }

  function setWishlist(items, detail) {
    var normalized = normalizeWishlist(items);
    localStorage.setItem('aykah_wishlist', JSON.stringify(normalized));
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: detail || { source: 'pdp' }
    }));
    return normalized;
  }

  function wishlistOptionKey(option, position) {
    var name = typeof option === 'string' ? option : option && option.name;
    var normalizedName = String(name || '').toLowerCase();

    if (/colou?r|finish|fabric|material/.test(normalizedName)) return 'colour';
    if (/size|seater/.test(normalizedName)) return 'size';
    if (/orientation|configuration|facing|chaise|direction/.test(normalizedName)) return 'orientation';

    /* Some sectional products use a generic option name for Left/Right Facing. */
    var values = option && Array.isArray(option.values) ? option.values : [];
    var sectional = state.productData && /sectional/i.test(state.productData.title || '');
    if (sectional && values.length) {
      var hasLeft = values.some(function (value) { return /(^|\b)(left|laf|lhf|lhs)(\b|$)/i.test(String(value)); });
      var hasRight = values.some(function (value) { return /(^|\b)(right|raf|rhf|rhs)(\b|$)/i.test(String(value)); });
      if (hasLeft && hasRight) return 'orientation';
    }

    return 'option' + position;
  }

  function currentWishlistRecord(handle) {
    var variants = state.productData && Array.isArray(state.productData.variants)
      ? state.productData.variants
      : [];
    var variant = variants.find(function (candidate) {
      return parseInt(candidate.id, 10) === parseInt(state.variantId, 10);
    }) || null;
    var descriptors = state.productData && Array.isArray(state.productData.options_with_values)
      ? state.productData.options_with_values
      : (state.productData && Array.isArray(state.productData.options) ? state.productData.options : []);
    var options = {};
    var usedKeys = Object.create(null);

    descriptors.forEach(function (option, index) {
      var position = option && option.position ? parseInt(option.position, 10) : index + 1;
      if (!position || position > 3) return;
      var value = variant
        ? variant['option' + position]
        : state.selectedOptions['option' + position];
      if (!value || value === 'Default Title') return;
      var key = wishlistOptionKey(option, position);
      if (usedKeys[key]) key = 'option' + position;
      usedKeys[key] = true;
      options[key] = String(value);
    });

    return {
      handle: handle,
      variantId: variant ? parseInt(variant.id, 10) : (parseInt(state.variantId, 10) || null),
      options: normalizeWishlistOptions(options),
      partial: false
    };
  }

  function bindWishlist() {
    var btns = qsa('[data-wishlist-toggle]');
    var handle = btns.length ? btns[0].dataset.productHandle : null;
    if (!handle) return;

    function syncCurrentVariantWishlistUI() {
      var current = currentWishlistRecord(handle);
      var currentKey = wishlistRecordKey(current);
      var isWishlisted = !!currentKey && getWishlist().some(function (saved) {
        return wishlistRecordKey(saved) === currentKey;
      });
      updateWishlistUI(isWishlisted);
    }

    syncCurrentVariantWishlistUI();

    btns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var items = getWishlist();
        var current = currentWishlistRecord(handle);
        var currentKey = wishlistRecordKey(current);
        var isSaved = !!currentKey && items.some(function (saved) {
          return wishlistRecordKey(saved) === currentKey;
        });

        if (isSaved) {
          /* Remove only the selected variant; other Aura colours/sizes stay saved. */
          items = items.filter(function (saved) { return wishlistRecordKey(saved) !== currentKey; });
        } else {
          /* Upgrade any legacy/card-level placeholder into this exact selection. */
          items = items.filter(function (saved) {
            return !(saved.handle === handle && wishlistRecordKey(saved) === handle + '::partial');
          });
          items.push(current);
        }

        setWishlist(items, {
          source: 'pdp',
          handle: handle,
          variantId: current.variantId,
          recordKey: currentKey,
          saved: !isSaved
        });
        syncCurrentVariantWishlistUI();
      });
    });

    /* Keep the heart scoped to the variant currently selected on the PDP. */
    document.addEventListener('wishlist:updated', syncCurrentVariantWishlistUI);
    document.addEventListener('product:variant-change', syncCurrentVariantWishlistUI);
  }

  /* Phosphor heart: swap outline (Regular) <-> solid (Fill) */
  window.AykahSetHeart = window.AykahSetHeart || function (svg, active) {
    if (!svg) return;
    svg.setAttribute('viewBox', '0 0 256 256');
    svg.setAttribute('fill', 'currentColor');
    svg.innerHTML = active
      ? '<path d="M240,102c0,70-103.79,126.66-108.21,129a8,8,0,0,1-7.58,0C119.79,228.66,16,172,16,102A62.07,62.07,0,0,1,78,40c20.65,0,38.73,8.88,50,23.89C139.27,48.88,157.35,40,178,40A62.07,62.07,0,0,1,240,102Z"/>'
      : '<path d="M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z"/>';
  };

  function updateWishlistUI(isWishlisted) {
    // Update all wishlist icons (swap Phosphor outline <-> fill)
    qsa('[data-wishlist-icon]').forEach(function (icon) {
      if (isWishlisted) {
        window.AykahSetHeart(icon, true);
        icon.classList.add('text-brand-error');
        icon.classList.remove('text-brand-text-muted');
      } else {
        window.AykahSetHeart(icon, false);
        icon.classList.remove('text-brand-error');
        icon.classList.add('text-brand-text-muted');
      }
    });

    // Update wishlist text buttons
    qsa('[data-wishlist-text]').forEach(function (el) {
      el.textContent = isWishlisted ? 'Saved to Wishlist' : 'Add to Wishlist';
    });

    // Update wishlist button styling (full-width button below ATC)
    qsa('[data-wishlist-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
      btn.setAttribute('aria-label', isWishlisted ? 'Remove from wishlist' : 'Add to wishlist');
      if (btn.dataset.productHandle) {
        if (isWishlisted) {
          btn.classList.remove('border-border', 'text-foreground', 'hover:border-foreground/40');
          btn.classList.add('border-brand-error/30', 'text-brand-error', 'bg-brand-error/5');
        } else {
          btn.classList.remove('border-brand-error/30', 'text-brand-error', 'bg-brand-error/5');
          btn.classList.add('border-border', 'text-foreground', 'hover:border-foreground/40');
        }
      }
    });
  }

  /* ── PRE-ORDER SAFETY NET ──────────────────────────────────────────────
     The pre-order app does not add a button, it REPLACES ours: it puts
     gPreorderHidden (display:none) on this theme's buy button and injects its
     own clone in the same slot. That trade only works if the second half
     happens. When the app's script is blocked, times out, or is dropped by a
     section re-render in the theme editor, the hide has already landed and the
     clone never arrives, so the page keeps the pre-order note and loses every
     button under it. A product whose entire purpose is being ordered ahead
     ends up with no way to order it, and it reads as "the buttons were
     removed" rather than as an app failure.

     So: if no visible pre-order button exists, ours goes back. While the app
     is working this does nothing at all and the app keeps the CTA, which is
     what we want, because only the app carries the pre-order line properties
     through to the order. The button is labelled server-side, so what comes
     back already reads Preorder and posts to /cart/add, which the variant
     accepts because it oversells. */
  /* Measured, not guessed: a MutationObserver watching from document_start
     records the app hiding our button and inserting its own in the SAME tick,
     0ms apart, on a fast connection and on throttled 3G alike. There is no
     window in which the page is legitimately buttonless, so this only has to
     stay out of the way until the app has had a chance to run at all. */
  var PREORDER_GRACE_MS = 1500;

  function visibleAppPreorderButton() {
    var buttons = document.querySelectorAll('.gPreorderBtn');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].offsetParent !== null) return buttons[i];
    }
    return null;
  }

  /* Globo owns the warehouse window and can set a different value for every
     product or variant. Read that live message instead of copying a date into
     the theme, then present the same value in a shorter notice above the CTA. */
  function warehouseWindowFromGlobo(message) {
    var text = String(message || '').replace(/\s+/g, ' ').trim();
    var match = text.match(
      /expected to reach (?:our )?BC warehouse(?:\s+between)?\s+(.+?)(?:\.\s|$)/i
    );
    if (!match) match = text.match(/\bbetween\s+(.+?)(?:\.\s|$)/i);
    return match ? match[1].replace(/\s*[-–]\s*/g, '–').trim() : '';
  }

  function clearPreorderSummary(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-aykah-preorder-summary]'),
      function (notice) { notice.remove(); }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('.aykah-preorder-source--hidden'),
      function (source) { source.classList.remove('aykah-preorder-source--hidden'); }
    );
  }

  /* Globo can fail to inject ANYTHING, in which case there is no message in the
     DOM to read and the shopper sees no dates at all.

     Measured on Cydra, 2026-09-21: Globo's own payload names Slate 71 as
     product.first_available_variant while omitting it from product.variants.
     Its profile map points at that variant, parseProfile looks it up, gets
     undefined, reads .id and throws. renderProductForm dies, so no button and
     no message are ever injected. The app admin looks perfectly configured,
     because it is: the fault is in the payload the storefront receives.

     The dates are still on the page, in GloboPreorderParams.preorderSettings.
     Read them from there instead of waiting for an injection that is not
     coming. Same source of truth the app itself would use, so nothing is
     guessed and no date is ever hardcoded into the theme. */
  function warehouseWindowFromParams() {
    var g = window.GloboPreorderParams;
    if (!g || !g.preorderSettings || !g.product) return '';

    var byProduct = g.preorderSettings.products && g.preorderSettings.products[g.product.id];
    if (!byProduct) return '';

    var input = document.querySelector('[data-purchase-panel] [name="id"]')
      || document.querySelector('[name="id"]');
    var variantId = input && input.value ? String(input.value).replace(/\D/g, '') : '';
    if (!variantId) return '';

    var profileId = byProduct[variantId];
    if (!profileId) return '';

    var profiles = g.preorderSettings.profiles || {};
    var profile = profiles[profileId] || profiles[String(profileId)];
    /* status 0 is a profile the team deliberately switched off. Honour that
       rather than reviving copy they retired. */
    if (!profile || String(profile.status) !== '1') return '';

    var message = profile.message;
    if (typeof message === 'string') {
      try { message = JSON.parse(message); } catch (e) { return ''; }
    }
    var above = message && message['default'] && message['default'].aboveMessage;
    return warehouseWindowFromGlobo(above);
  }

  /* Idempotent on purpose. A MutationObserver drives the caller, so creating or
     moving a node on every pass would retrigger the observer and spin. Reuse
     the existing notice and only touch the DOM when something actually differs. */
  function renderPreorderSummary(button, warehouseWindow) {
    if (!button || !warehouseWindow) return;
    var panel = button.closest('[data-purchase-panel]');
    if (!panel || !button.parentNode) return;

    var notice = panel.querySelector('[data-aykah-preorder-summary]');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'aykah-preorder-summary';
      notice.setAttribute('data-aykah-preorder-summary', '');
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');

      var headline = document.createElement('p');
      headline.className = 'aykah-preorder-summary__headline';
      headline.setAttribute('data-aykah-preorder-headline', '');
      notice.appendChild(headline);

      var detail = document.createElement('p');
      detail.className = 'aykah-preorder-summary__detail';
      detail.textContent = 'Shipping and store pickup will begin shortly after arrival.';
      notice.appendChild(detail);
    }

    var headline = notice.querySelector('[data-aykah-preorder-headline]');
    var headlineText =
      'Pre-order item. Expected at our BC warehouse ' + warehouseWindow + '.';
    if (headline.textContent !== headlineText) headline.textContent = headlineText;

    /* Insert immediately above whichever button is acting as the CTA. Reliable
       whether that is Globo's injected control or the theme's own. */
    if (notice.parentNode !== button.parentNode || notice.nextSibling !== button) {
      button.parentNode.insertBefore(notice, button);
    }
  }

  /* The normal path: Globo injected its message, so read the window out of it
     and hide the app's longer copy behind our shorter line. */
  function syncPreorderSummary(button) {
    if (!button) return;

    var panel = button.closest('[data-purchase-panel]');
    if (!panel || !button.parentNode) return;

    var sources = panel.querySelectorAll(
      '.gPreorderBottomMessage, .gPreorderTopMessage'
    );
    var warehouseWindow = '';

    for (var i = 0; i < sources.length; i++) {
      warehouseWindow = warehouseWindowFromGlobo(sources[i].textContent);
      if (warehouseWindow) break;
    }

    /* If Globo changes its wording, keep its original message visible. A stale
       or guessed warehouse date is worse than the longer app copy. */
    if (!warehouseWindow) return;

    renderPreorderSummary(button, warehouseWindow);

    Array.prototype.forEach.call(sources, function (source) {
      source.classList.add('aykah-preorder-source--hidden');
    });
  }

  /* The app's clone carries data-add-to-cart too, so the theme's original is
     everything with that hook EXCEPT anything wearing the app's class. */
  function themeBuyButtons() {
    return document.querySelectorAll('[data-add-to-cart]:not(.gPreorderBtn)');
  }

  function showThemeBuyButtons() {
    var b = themeBuyButtons();
    for (var i = 0; i < b.length; i++) {
      b[i].classList.remove('gPreorderHidden');
      if (b[i].style.display === 'none') b[i].style.removeProperty('display');
    }
  }

  function hideThemeBuyButtons() {
    var b = themeBuyButtons();
    for (var i = 0; i < b.length; i++) b[i].classList.add('gPreorderHidden');
  }

  /* Exactly one buy button, whichever one is available.

     Deliberately stateless. An earlier version restored ours once and then
     latched, so that if the app hid it again nothing put it back: the button
     appeared and then vanished for good, which is worse than the failure it
     was meant to cover. Re-deriving the answer from the DOM every time means
     the page self-corrects in both directions no matter what the app does or
     in what order, and repeating a class the element already has costs
     nothing. */
  function reconcileBuyButton(startedAt) {
    var appButton = visibleAppPreorderButton();
    if (appButton) {
      hideThemeBuyButtons(); // the app owns the CTA; only its button carries
      syncPreorderSummary(appButton);
      return;                // pre-order properties through to the order
    }

    var scope = document.querySelector('[data-purchase-panel]') || document;

    /* Still inside the grace window: the app may yet arrive, so show nothing
       rather than flashing our fallback and then handing over. */
    if (Date.now() - startedAt < PREORDER_GRACE_MS) {
      clearPreorderSummary(scope);
      return;
    }
    showThemeBuyButtons();

    /* The app is not coming. If the theme's own inventory check already put
       this variant in pre-order (data-is-preorder, set server side), build the
       notice from the app's data rather than leaving the shopper with a
       Preorder button and no dates at all.

       Gated on the server-side flag, not on the button's label, so a wording
       change in the theme settings cannot silently switch this on or off. */
    var themeButton = document.querySelector(
      '[data-add-to-cart][data-is-preorder="true"]:not(.gPreorderBtn)'
    );
    var fallbackWindow = themeButton ? warehouseWindowFromParams() : '';

    if (fallbackWindow) {
      renderPreorderSummary(themeButton, fallbackWindow);
    } else {
      clearPreorderSummary(scope);
    }
  }

  /* Watch rather than check once: the app hides the button on its own
     schedule, so a single pass on load would run before there is anything to
     undo. Polling stops after 15s; re-renders are picked up by the section
     event instead. Guarded so a second copy of this asset cannot double-bind. */
  function watchBuyButton() {
    if (window.__aykahPreorderNetBound) return;
    window.__aykahPreorderNetBound = true;

    var startedAt = Date.now();
    var queued = false;

    /* Coalesce to one pass per frame. The observer fires on our own class
       changes too, so reconciling inline would recurse. */
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        reconcileBuyButton(startedAt);
      });
    }

    /* Event-driven rather than a timer with an end. A timer only protects the
       page for as long as it happens to run, and the app can hide the button
       at any point later, on a variant change or a re-render. The observer has
       no expiry, so a late hide is still answered. */
    var section = document.querySelector('.section-product-hero') || document.body;
    new MutationObserver(schedule).observe(section, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      characterData: true,
      subtree: true
    });

    /* The observer only reports changes, so the first pass has to be asked
       for. Poll briefly as well: the app may run before the observer is
       attached, and nothing would have been reported. */
    var ticks = 0;
    var warmup = setInterval(function () {
      schedule();
      if (++ticks >= 30) clearInterval(warmup); // 15s
    }, 500);

    // A re-render replaces the markup and replays the app's injection.
    document.addEventListener('shopify:section:load', function () {
      startedAt = Date.now();
      schedule();
    });
  }

  /* ── BOOT ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      watchBuyButton();
    });
  } else {
    init();
    watchBuyButton();
  }
})();
